<template>
  <content-with-sidebar
      class="cws-container cws-sidebar-right blog-wrapper"
  >

    <!-- content -->
    <div class="blog-detail-wrapper" v-if="artistDetail">
      <b-row>
        <!-- blogs -->
        <b-col cols="12">
          <b-card
              _img-top
              _img-alt="Постер"
              _:title="`${artistDetail.title}`"
          >
            <h1 class="d-flex align-items-center">
              <template v-if="!isEditingTitle">
                {{ artistDetail.title }}
                <b-button
                    variant="flat-primary"
                    class="btn-icon ml-50"
                    size="sm"
                    @click="startEditingTitle"
                >
                  <feather-icon icon="Edit2Icon"/>
                </b-button>
              </template>
              <template v-else>
                <b-form-input
                    v-model="editTitle"
                    class="mr-1"
                    style="font-size: inherit; max-width: 400px;"
                    @keyup.enter="saveTitle"
                />
                <b-button
                    variant="success"
                    class="btn-icon mr-50"
                    size="sm"
                    @click="saveTitle"
                >
                  <feather-icon icon="CheckIcon"/>
                </b-button>
                <b-button
                    variant="flat-secondary"
                    class="btn-icon"
                    size="sm"
                    @click="cancelEditingTitle"
                >
                  <feather-icon icon="XIcon"/>
                </b-button>
              </template>
              <div class="d-flex ml-auto">
                <span class="font-weight-bold font-large-2 ml-1">
                  <div class="small">{{ artistDetail.popularity }}</div>
                </span>
              </div>
            </h1>

            <div v-if="aliasesText" class="text-muted font-small-4 mb-1">
              <span class="font-weight-bold mr-50">Также известен как:</span>
              <span>{{ aliasesText }}</span>
            </div>

            <b-row>
              <b-col
                  cols="12"
                  md="12"
                  class="d-flex align-items-center justify-content-center mb-2 mb-md-0"
              >
                <b-card
                    title="Исполнитель"
                    class="col-md-12 col-12"
                >
                  <b-overlay
                      :show="isLoadingArtist"
                      rounded="sm"
                  >
                    <b-row>
                      <b-col md="12">

                        <!-- Информация об артисте -->
                        <div class="artist-info-block mb-1 p-1 rounded" style="background-color: rgba(115, 103, 240, 0.05); border: 1px solid rgba(115, 103, 240, 0.15);">
                          <div class="d-flex flex-wrap align-items-center" style="gap: 0.75rem 1.5rem;">
                            <div class="d-flex align-items-center">
                              <span class="text-muted font-small-3 mr-50">ID:</span>
                              <strong class="font-small-3">{{ artistDetail.id }}</strong>
                            </div>
                            <div class="d-flex align-items-center">
                              <span class="text-muted font-small-3 mr-50">Язык:</span>
                              <b-badge :variant="artistDetail.russian ? 'light-success' : 'light-secondary'" class="font-small-2">
                                {{ artistDetail.russian ? 'Русский' : 'Зарубежный' }}
                              </b-badge>
                            </div>
                            <div class="d-flex align-items-center">
                              <span class="text-muted font-small-3 mr-50">Треков:</span>
                              <strong class="font-small-3">{{ artistDetail.downloaded_tracks_count || 0 }}</strong>
                              <span class="text-muted font-small-3 mx-25">/</span>
                              <span class="font-small-3">{{ artistDetail.tracks_count || 0 }}</span>
                            </div>
                            <div v-if="artistDetail.onehitwonder" class="d-flex align-items-center">
                              <b-badge variant="light-warning" class="font-small-2">One Hit Wonder</b-badge>
                            </div>
                            <div v-if="artistDetail.notion" class="d-flex align-items-center">
                              <b-badge variant="light-info" class="font-small-2">Notion</b-badge>
                            </div>
                            <div class="d-flex align-items-center ml-md-auto" style="gap: 1rem;">
                              <b-form-checkbox
                                  :checked="!!artistDetail.skip_originals"
                                  switch
                                  @change="toggleSkipOriginals"
                              >
                                Пропускать оригиналы
                              </b-form-checkbox>
                              <b-form-checkbox
                                  :checked="!!artistDetail.is_cover_artist"
                                  switch
                                  @change="toggleCoverArtist"
                              >
                                Кавер-артист
                              </b-form-checkbox>
                            </div>
                          </div>
                          <!-- Сложность -->
                          <div
                              class="d-flex flex-wrap align-items-center mt-75 pt-75"
                              style="gap: 0.5rem 1.5rem; border-top: 1px solid rgba(115, 103, 240, 0.1);"
                          >
                            <div class="d-flex align-items-center">
                              <span class="text-muted font-small-3 mr-50">Сложность:</span>
                              <b-form-rating
                                  :value="artistDetail.difficulty_rating"
                                  :stars="10"
                                  inline
                                  no-border
                                  size="sm"
                                  variant="warning"
                                  class="p-0"
                                  @change="updateDifficultyRating"
                              />
                              <span
                                  v-if="artistDetail.calculated_difficulty != null"
                                  class="text-muted ml-50"
                                  style="font-size: 1.285rem;"
                                  v-b-tooltip.hover
                                  title="Расчётная сложность"
                              >({{ artistDetail.calculated_difficulty }})</span>
                              <feather-icon
                                  icon="HelpCircleIcon"
                                  size="14"
                                  class="text-muted ml-25"
                                  v-b-tooltip.hover.html="difficultyTooltip"
                              />
                              <span v-if="artistDetail.difficulty_rating == null" class="text-muted font-small-3 ml-50">не задана</span>
                            </div>
                          </div>
                          <!-- Внешние ссылки -->
                          <div
                              class="d-flex flex-wrap align-items-center mt-75 pt-75"
                              style="gap: 0.5rem; border-top: 1px solid rgba(115, 103, 240, 0.1);"
                          >
                            <span class="text-muted font-small-3 mr-25">Ссылки:</span>

                            <!-- SHS -->
                            <span v-if="artistDetail.external_ids && artistDetail.external_ids.second_hand_songs" class="d-inline-flex align-items-center">
                              <b-badge
                                  tag="a"
                                  variant="light-info"
                                  class="font-small-2"
                                  :href="'https://secondhandsongs.com/artist/' + artistDetail.external_ids.second_hand_songs"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style="cursor: pointer; border-top-right-radius: 0; border-bottom-right-radius: 0;"
                              >
                                SHS: {{ artistDetail.external_ids.second_hand_songs }}
                                <feather-icon icon="ExternalLinkIcon" size="11" class="ml-25" />
                              </b-badge>
                              <b-button
                                  variant="flat-danger"
                                  class="btn-icon p-0"
                                  size="sm"
                                  style="line-height: 1; margin-left: -2px;"
                                  @click="deleteExternalId('second_hand_songs')"
                              >
                                <feather-icon icon="XIcon" size="12" />
                              </b-button>
                            </span>
                            <b-button
                                v-else
                                variant="outline-info"
                                size="sm"
                                class="py-25 px-50 font-small-2"
                                :disabled="shsSyncDisabled"
                                @click="syncShs"
                            >
                              <feather-icon icon="SearchIcon" size="11" class="mr-25" />
                              SHS
                            </b-button>

                            <!-- Spotify -->
                            <span v-if="artistDetail.external_ids && artistDetail.external_ids.spotify" class="d-inline-flex align-items-center">
                              <b-badge
                                  tag="a"
                                  variant="light-success"
                                  class="font-small-2"
                                  :href="'https://open.spotify.com/artist/' + artistDetail.external_ids.spotify"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style="cursor: pointer; border-top-right-radius: 0; border-bottom-right-radius: 0;"
                              >
                                Spotify: {{ artistDetail.external_ids.spotify }}
                                <feather-icon icon="ExternalLinkIcon" size="11" class="ml-25" />
                              </b-badge>
                              <b-button
                                  variant="flat-danger"
                                  class="btn-icon p-0"
                                  size="sm"
                                  style="line-height: 1; margin-left: -2px;"
                                  @click="deleteExternalId('spotify')"
                              >
                                <feather-icon icon="XIcon" size="12" />
                              </b-button>
                            </span>
                            <b-button
                                v-else
                                variant="outline-success"
                                size="sm"
                                class="py-25 px-50 font-small-2"
                                @click="syncSpotify"
                            >
                              <feather-icon icon="SearchIcon" size="11" class="mr-25" />
                              Spotify
                            </b-button>

                            <!-- Yandex Music -->
                            <span v-if="artistDetail.external_ids && artistDetail.external_ids.yandex" class="d-inline-flex align-items-center">
                              <b-badge
                                  tag="a"
                                  variant="light-danger"
                                  class="font-small-2"
                                  :href="'https://music.yandex.ru/artist/' + artistDetail.external_ids.yandex"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style="cursor: pointer; border-top-right-radius: 0; border-bottom-right-radius: 0;"
                              >
                                Yandex: {{ artistDetail.external_ids.yandex }}
                                <feather-icon icon="ExternalLinkIcon" size="11" class="ml-25" />
                              </b-badge>
                              <b-button
                                  variant="flat-danger"
                                  class="btn-icon p-0"
                                  size="sm"
                                  style="line-height: 1; margin-left: -2px;"
                                  @click="deleteExternalId('yandex')"
                              >
                                <feather-icon icon="XIcon" size="12" />
                              </b-button>
                            </span>
                            <b-button
                                v-else
                                variant="outline-danger"
                                size="sm"
                                class="py-25 px-50 font-small-2"
                                @click="syncYandex"
                            >
                              <feather-icon icon="SearchIcon" size="11" class="mr-25" />
                              Yandex
                            </b-button>

                            <!-- YouTube -->
                            <span v-if="artistDetail.external_ids && artistDetail.external_ids.youtube" class="d-inline-flex align-items-center">
                              <b-badge
                                  tag="a"
                                  class="font-small-2"
                                  :href="'https://www.youtube.com/channel/' + artistDetail.external_ids.youtube"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style="cursor: pointer; background-color: #ff0000 !important; border-top-right-radius: 0; border-bottom-right-radius: 0;"
                              >
                                YouTube: {{ artistDetail.external_ids.youtube }}
                                <feather-icon icon="ExternalLinkIcon" size="11" class="ml-25" />
                              </b-badge>
                              <b-button
                                  variant="flat-danger"
                                  class="btn-icon p-0"
                                  size="sm"
                                  style="line-height: 1; margin-left: -2px;"
                                  @click="deleteExternalId('youtube')"
                              >
                                <feather-icon icon="XIcon" size="12" />
                              </b-button>
                            </span>
                            <b-button
                                v-else
                                variant="outline-danger"
                                size="sm"
                                class="py-25 px-50 font-small-2"
                                disabled
                                style="border-color: #ff0000 !important; color: #ff0000 !important;"
                            >
                              <feather-icon icon="SearchIcon" size="11" class="mr-25" />
                              YouTube
                            </b-button>

                            <!-- Wikipedia -->
                            <span v-if="artistDetail.external_ids && artistDetail.external_ids.wikipedia" class="d-inline-flex align-items-center">
                              <b-badge
                                  tag="a"
                                  variant="light-secondary"
                                  class="font-small-2"
                                  :href="wikipediaUrl(artistDetail.external_ids.wikipedia)"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style="cursor: pointer; border-top-right-radius: 0; border-bottom-right-radius: 0;"
                              >
                                {{ wikipediaLabel(artistDetail.external_ids.wikipedia) }}
                                <feather-icon icon="ExternalLinkIcon" size="11" class="ml-25" />
                              </b-badge>
                              <b-button
                                  variant="flat-danger"
                                  class="btn-icon p-0"
                                  size="sm"
                                  style="line-height: 1; margin-left: -2px;"
                                  @click="deleteExternalId('wikipedia')"
                              >
                                <feather-icon icon="XIcon" size="12" />
                              </b-button>
                            </span>
                            <b-button
                                v-else
                                variant="outline-secondary"
                                size="sm"
                                class="py-25 px-50 font-small-2"
                                @click="syncWikipedia"
                            >
                              <feather-icon icon="SearchIcon" size="11" class="mr-25" />
                              Wiki
                            </b-button>

                            <!-- Добавить внешний ID -->
                            <b-button
                                v-b-tooltip.hover
                                title="Добавить внешний ID"
                                variant="flat-primary"
                                class="btn-icon p-25"
                                size="sm"
                                @click="showAddExternalIdModal = true"
                            >
                              <feather-icon icon="PlusCircleIcon" size="14"/>
                            </b-button>
                          </div>

                          <!-- Modal: Добавить внешний ID артисту -->
                          <b-modal
                              v-model="showAddExternalIdModal"
                              title="Добавить внешний ID"
                              ok-title="Добавить"
                              cancel-title="Отмена"
                              centered
                              :ok-disabled="!externalIdInput.trim() || isAddingExternalId"
                              :busy="isAddingExternalId"
                              @ok.prevent="addExternalId"
                              @hidden="externalIdInput = ''"
                          >
                            <b-form-group
                                label="Ссылка или идентификатор"
                                description="Вставьте ссылку на артиста (Spotify, YouTube, Yandex Music, SHS, Wikipedia) или идентификатор"
                            >
                              <b-form-input
                                  v-model="externalIdInput"
                                  placeholder="https://open.spotify.com/artist/... или ID"
                                  autofocus
                                  @keyup.enter="addExternalId"
                              />
                            </b-form-group>
                          </b-modal>
                        </div>

                        <div class="mb-1 d-flex align-items-center flex-wrap">
                          <b-button variant="primary" class="mr-1"
                                    @click="loadLastfmInfo"
                          >
                            <feather-icon
                                icon="TvIcon"
                                class="mr-50"
                            />
                            <span class="align-middle">LastFM</span>
                          </b-button>
                          <b-button
                              class="mr-1"
                              @click="ignoreTracks"
                          >
                            <feather-icon
                                icon="UsersIcon"
                                class="mr-50"
                            />
                            <span class="align-middle">Игнорировать пропущенное</span>
                          </b-button>
                          <b-button
                              v-if="showShsTracksButton"
                              class="mr-1"
                              @click="syncShsTracks"
                              style="background-color: #019caf !important;"
                          >
                            <feather-icon
                                icon="MusicIcon"
                                class="mr-50"
                            />
                            <span class="align-middle">SHS Треки</span>
                          </b-button>
                          <b-button
                              variant="outline-danger"
                              class="mr-1"
                              @click="flushCache"
                          >
                            <feather-icon
                                icon="TrashIcon"
                                class="mr-50"
                            />
                            <span class="align-middle">Очистить кеш</span>
                          </b-button>
                        </div>

                        <b-tabs
                            pills
                        >
                          <!-- Tab: Песни (таблица) -->
                          <b-tab>
                            <template #title>
                              <feather-icon
                                  icon="Share2Icon"
                                  size="16"
                                  class="mr-0 mr-sm-50"
                              />
                              <span class="d-none d-sm-inline">Песни [{{ artistDetail.tracks.length}}]</span>
                            </template>

                            <div class="my-1 py-25">
                              <b-table
                                  ref="refTracksListTable"
                                  :items="artistDetail.tracks"
                                  responsive
                                  :fields="tableColumns"
                                  :tbody-tr-class="rowClass"
                                  primary-key="id"
                                  show-empty
                                  empty-text="No tracks"
                                  class="position-relative"
                              >

                                <!-- Column: Client -->
                                <template #cell(artist)="data">
                                  <span class="text-nowrap">
                                    {{ data.item.artist }}
                                  </span>
                                </template>

                                <!-- Column: Release year -->
                                <template #cell(release_year)="data">
                                  <span class="text-nowrap">
                                    {{ data.item.release_year || '—' }}
                                  </span>
                                </template>

                                <!-- Column: Client -->
                                <template #cell(play_count)="data">
                                  <span class="text-nowrap">
                                    {{ formatNumber(data.item.play_count) }}
                                  </span>
                                </template>

                                <!-- Column: Info (External IDs) -->
                                <template #cell(info)="data">
                                  <span class="text-nowrap" v-if="data.item.external_ids">
                                    <b-badge
                                        v-if="data.item.external_ids.spotify"
                                        v-b-tooltip.hover
                                        :title="'Spotify: ' + data.item.external_ids.spotify"
                                        tag="a"
                                        :href="'https://open.spotify.com/track/' + data.item.external_ids.spotify"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        variant="success"
                                        class="mr-25 cursor-pointer"
                                        style="font-size: 0.7rem;"
                                    >SP</b-badge>
                                    <b-badge
                                        v-if="data.item.external_ids.yandex"
                                        v-b-tooltip.hover
                                        :title="'Yandex Music: ' + data.item.external_ids.yandex"
                                        tag="a"
                                        :href="'https://music.yandex.ru/track/' + data.item.external_ids.yandex"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        variant="danger"
                                        class="mr-25 cursor-pointer"
                                        style="font-size: 0.7rem;"
                                    >YA</b-badge>
                                    <b-badge
                                        v-if="data.item.external_ids.youtube"
                                        v-b-tooltip.hover
                                        :title="'YouTube: ' + data.item.external_ids.youtube"
                                        tag="a"
                                        :href="'https://www.youtube.com/watch?v=' + data.item.external_ids.youtube"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        variant="danger"
                                        class="mr-25 cursor-pointer"
                                        style="font-size: 0.7rem; background-color: #ff0000 !important;"
                                    >YT</b-badge>
                                    <b-badge
                                        v-if="data.item.external_ids.second_hand_songs"
                                        v-b-tooltip.hover
                                        :title="'SHS: ' + data.item.external_ids.second_hand_songs"
                                        tag="a"
                                        :href="'https://secondhandsongs.com/performance/' + data.item.external_ids.second_hand_songs"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        variant="info"
                                        class="mr-25 cursor-pointer"
                                        style="font-size: 0.7rem;"
                                    >SH</b-badge>
                                  </span>
                                </template>

                                <!-- Column: Client -->
                                <template #cell(covers)="data">
                                  <b-button
                                      v-if="data.item.has_covers"
                                      v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                                      variant="success"
                                      class="btn-icon"
                                      :to="{name: 'track-detail', params: { id: data.item.id }, query: { tab: 'covers' }}"
                                  >
                                    <feather-icon icon="DiscIcon"/>
                                  </b-button>
                                  <b-button
                                      v-else-if="data.item.is_cover"
                                      v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                                      variant="warning"
                                      class="btn-icon"
                                      :to="{name: 'track-detail', params: { id: data.item.id }, query: { tab: 'covers' }}"
                                  >
                                    <feather-icon icon="DiscIcon"/>
                                  </b-button>
                                  <b-button
                                      v-else
                                      v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                                      variant="secondary"
                                      class="btn-icon"
                                      :to="{name: 'track-detail', params: { id: data.item.id }, query: { tab: 'covers' }}"
                                  >
                                    <feather-icon icon="DiscIcon"/>
                                  </b-button>
                                </template>

                                <template #cell(actions)="data">
                                  <span class="text-nowrap">
                                    <b-button
                                        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                                        variant="warning"
                                        class="btn-icon ml-1"
                                        :to="{name: 'track-detail', params: { id: data.item.id }}"
                                    >
                                    <feather-icon icon="DownloadIcon"/>
                                    </b-button>
                                    <b-button
                                        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                                        :variant="data.item.file ? `success` : ``"
                                        :disabled="!data.item.file"
                                        class="btn-icon ml-1"
                                        :href="data.item.url"
                                    >
                                      <feather-icon :icon="data.item.file ? `SaveIcon` : `FileIcon`"/>
                                    </b-button>
                                    <b-button
                                        :variant="data.item.ignore ? 'danger' : 'primary'"
                                        class="btn-icon ml-1"
                                        @click="toggleIgnore(data.item)"
                                    >
                                      <feather-icon :icon="(data.item.ignore) ? 'EyeOffIcon' : 'EyeIcon'"/>
                                    </b-button>
                                    <b-button
                                        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                                        variant="danger"
                                        class="btn-icon ml-1"
                                        @click="deleteTrack(data.item.id)"
                                    >
                                      <feather-icon icon="Trash2Icon"/>
                                    </b-button>
                                  </span>

                                </template>
                              </b-table>

                              <b-button variant="primary" class="mr-1"
                                        @click="loadAdditionalTracks"
                              >
                                <feather-icon
                                    icon="PlusSquareIcon"
                                    class="mr-50"
                                />
                                <span class="align-middle">Дополнительные треки</span>
                              </b-button>

                              <b-input-group class="d-inline-flex ml-1" style="width: 350px; vertical-align: middle;">
                                <b-form-input
                                    v-model="trackSearchQuery"
                                    placeholder="Поиск трека по названию..."
                                    size="sm"
                                    @keyup.enter="searchTracksByName"
                                />
                                <b-input-group-append>
                                  <b-button variant="info" size="sm" @click="searchTracksByName">
                                    <feather-icon icon="SearchIcon" class="mr-50" />
                                    <span class="align-middle">Поиск</span>
                                  </b-button>
                                </b-input-group-append>
                              </b-input-group>

                              <div v-if="additional">
                                <div v-for="track in additional" class="d-flex flex-md-row flex-column mt-1">
                                  <b-button
                                      variant="warning"
                                      class="btn-icon mr-1"
                                      size="sm"
                                      @click="addTrack(track.title)"
                                  >
                                    <feather-icon icon="PlusIcon"/>
                                  </b-button>

                                  <span class="font-weight-bold d-block d-flex align-items-center">
                                    <span class="text-success">
                                      {{ track.artist }} - {{ track.title }} [{{ track.popularity }}]
                                    </span>
                                    <b-form-rating
                                        :value="toStarRating(track.popularity)"
                                        readonly
                                        inline
                                        no-border
                                        half
                                        size="sm"
                                        variant="warning"
                                        class="p-0 ml-50"
                                    />
                                  </span>

                                </div>
                              </div>

                            </div>
                          </b-tab>


                          <!-- Tab: Списки -->
                          <b-tab>
                            <template #title>
                              <feather-icon
                                  icon="Share2Icon"
                                  size="16"
                                  class="mr-0 mr-sm-50"
                              />
                              <span class="d-none d-sm-inline">Списки</span>
                              <b-badge pill variant="light-primary" class="ml-50">
                                {{ artistDetail.lists_count || 0 }}
                              </b-badge>
                            </template>

                            <div class="my-1 py-25">
                              <b-row
                                  class="m-1"
                                  v-for="list in artistDetail.lists"
                                  :key="list.id"
                              >
                                <b-col md="12">
                                  <div class="d-flex align-items-center mt-0">
                                    <div class="user-page-info">
                                      <h6 class="mb-0">
                                        <feather-icon
                                            icon="ChevronRightIcon"
                                            size="21"
                                        />
                                        {{ list.title }}
                                      </h6>
                                    </div>
                                  </div>
                                </b-col>
                              </b-row>
                            </div>
                          </b-tab>

                          <!-- Tab: SHS Треки -->
                          <b-tab
                              v-if="artistDetail.external_ids && artistDetail.external_ids.second_hand_songs != null"
                              @click="fetchShsTracks"
                          >
                            <template #title>
                              <feather-icon
                                  icon="MusicIcon"
                                  size="16"
                                  class="mr-0 mr-sm-50"
                              />
                              <span class="d-none d-sm-inline">SHS Треки</span>
                            </template>

                            <div class="my-1 py-25">
                              <div v-if="shsTracksError" class="text-danger mb-1">{{ shsTracksError }}</div>
                              <b-table
                                  v-if="mergedShsTracks.length"
                                  :items="mergedShsTracks"
                                  responsive
                                  :fields="shsTableColumns"
                                  show-empty
                                  empty-text="Нет треков"
                                  class="position-relative"
                              >
                                <template #cell(db_title)="data">
                                  <router-link
                                      v-if="data.item.db_title && data.item.track_id"
                                      :to="{name: 'track-detail', params: { id: data.item.track_id }, query: { tab: 'covers' }}"
                                  >
                                    {{ data.item.db_title }}
                                  </router-link>
                                </template>

                                <template #cell(shs_title)="data">
                                  <span>{{ data.item.shs_title }}</span>
                                </template>

                                <template #cell(is_original)="data">
                                  <b-badge v-if="data.item.is_original !== null" :variant="data.item.is_original ? 'success' : 'warning'">
                                    {{ data.item.is_original ? 'Оригинал' : 'Кавер' }}
                                  </b-badge>
                                </template>

                                <template #cell(actions)="data">
                                  <div v-if="data.item.source === 'shs'" class="d-flex">
                                    <b-button
                                        variant="success"
                                        class="btn-icon mr-25"
                                        size="sm"
                                        @click="addShsTrack(data.item)"
                                        v-b-tooltip.hover
                                        title="Создать новый трек"
                                    >
                                      <feather-icon icon="PlusIcon"/>
                                    </b-button>
                                    <b-button
                                        variant="warning"
                                        class="btn-icon"
                                        size="sm"
                                        @click="openLinkShsTrackModal(data.item)"
                                        v-b-tooltip.hover
                                        title="Привязать к существующему"
                                    >
                                      <feather-icon icon="SearchIcon"/>
                                    </b-button>
                                  </div>
                                </template>

                                <template #cell(uri)="data">
                                  <b-button
                                      v-if="data.item.uri"
                                      variant="flat-primary"
                                      class="btn-icon"
                                      size="sm"
                                      :href="data.item.uri"
                                      target="_blank"
                                  >
                                    <feather-icon icon="ExternalLinkIcon"/>
                                  </b-button>
                                </template>
                              </b-table>
                              <div v-else-if="!shsTracksError" class="text-muted">Нажмите на вкладку для загрузки</div>
                            </div>
                          </b-tab>

                          <!-- Tab: Алиасы -->
                          <b-tab @click="fetchAliases">
                            <template #title>
                              <feather-icon
                                  icon="UsersIcon"
                                  size="16"
                                  class="mr-0 mr-sm-50"
                              />
                              <span class="d-none d-sm-inline">Алиасы</span>
                            </template>

                            <div class="my-1 py-25">
                              <div v-if="artistAliases.length">
                                <div
                                    v-for="alias in artistAliases"
                                    :key="alias.id"
                                    class="d-flex align-items-center mt-1"
                                >
                                  <feather-icon
                                      icon="ChevronRightIcon"
                                      size="16"
                                      class="mr-50"
                                  />
                                  <span>{{ alias.alias }}</span>
                                </div>
                              </div>
                              <div v-else class="text-muted">Нет алиасов</div>

                              <div class="d-flex align-items-center mt-1">
                                <b-button
                                    variant="success"
                                    class="btn-icon mr-1"
                                    @click="addManualAlias"
                                    :disabled="!manualAliasName.trim()"
                                >
                                  <feather-icon icon="UserPlusIcon"/>
                                </b-button>
                                <b-form-input
                                    v-model="manualAliasName"
                                    placeholder="Имя алиаса"
                                />
                              </div>

                              <b-button
                                  variant="primary"
                                  class="mt-1"
                                  @click="searchForAliases"
                              >
                                <feather-icon
                                    icon="SearchIcon"
                                    class="mr-50"
                                />
                                <span class="align-middle">Поиск</span>
                              </b-button>

                              <div v-if="aliasSearchResults.length" class="mt-1">
                                <h6>Результаты поиска:</h6>
                                <div
                                    v-for="result in aliasSearchResults"
                                    :key="result.name"
                                    class="d-flex align-items-center mt-1"
                                >
                                  <b-button
                                      variant="success"
                                      class="btn-icon mr-1"
                                      size="sm"
                                      @click="addAliasFromSearch(result.name)"
                                  >
                                    <feather-icon icon="UserPlusIcon"/>
                                  </b-button>
                                  <span>{{ result.name }}</span>
                                </div>
                              </div>
                            </div>
                          </b-tab>

                          <!-- Tab: Состав -->
                          <b-tab @click="initMembers">
                            <template #title>
                              <feather-icon
                                  :icon="hasMembers ? 'UsersIcon' : 'UserXIcon'"
                                  :class="hasMembers ? 'text-success' : 'text-muted'"
                                  size="16"
                                  class="mr-0 mr-sm-50"
                              />
                              <span class="d-none d-sm-inline">Состав</span>
                            </template>

                            <div class="my-1 py-25">
                              <!-- Кнопки действий -->
                              <div class="d-flex mb-1" style="gap: 0.5rem;">
                                <b-button
                                    variant="outline-primary"
                                    size="sm"
                                    :disabled="isLoadingMembers"
                                    @click="loadMembers"
                                >
                                  <b-spinner v-if="isLoadingMembers" small class="mr-50" />
                                  <feather-icon v-else icon="DownloadCloudIcon" class="mr-50" />
                                  Загрузить через AI
                                </b-button>
                                <b-button
                                    variant="success"
                                    size="sm"
                                    :disabled="isSavingMembers"
                                    @click="saveMembers"
                                >
                                  <b-spinner v-if="isSavingMembers" small class="mr-50" />
                                  <feather-icon v-else icon="SaveIcon" class="mr-50" />
                                  Сохранить
                                </b-button>
                              </div>

                              <b-row>
                                <!-- Текущие участники -->
                                <b-col md="6">
                                  <h6 class="mt-1">Текущие участники</h6>
                                  <div
                                      v-for="(member, idx) in currentMembers"
                                      :key="'cur-' + idx"
                                      class="d-flex align-items-center mb-50"
                                      style="gap: 0.25rem;"
                                  >
                                    <b-button
                                        variant="flat-secondary"
                                        class="btn-icon p-25"
                                        size="sm"
                                        :disabled="idx === 0"
                                        @click="moveMemberUp(currentMembers, idx)"
                                    >
                                      <feather-icon icon="ChevronUpIcon" size="14" />
                                    </b-button>
                                    <b-button
                                        variant="flat-secondary"
                                        class="btn-icon p-25"
                                        size="sm"
                                        :disabled="idx === currentMembers.length - 1"
                                        @click="moveMemberDown(currentMembers, idx)"
                                    >
                                      <feather-icon icon="ChevronDownIcon" size="14" />
                                    </b-button>
                                    <b-form-input
                                        v-model="currentMembers[idx]"
                                        size="sm"
                                        class="flex-grow-1"
                                    />
                                    <b-button
                                        variant="flat-warning"
                                        class="btn-icon p-25"
                                        size="sm"
                                        v-b-tooltip.hover
                                        title="В бывшие"
                                        @click="moveToFormer(idx)"
                                    >
                                      <feather-icon icon="ArrowRightIcon" size="14" />
                                    </b-button>
                                    <b-button
                                        variant="flat-danger"
                                        class="btn-icon p-25"
                                        size="sm"
                                        @click="removeMember(currentMembers, idx)"
                                    >
                                      <feather-icon icon="XIcon" size="14" />
                                    </b-button>
                                  </div>
                                  <div v-if="!currentMembers.length" class="text-muted mb-50">Нет текущих участников</div>
                                  <div class="d-flex align-items-center mt-50" style="gap: 0.25rem;">
                                    <b-form-input
                                        v-model="newCurrentMember"
                                        size="sm"
                                        placeholder="Новый участник"
                                        class="flex-grow-1"
                                        @keyup.enter="addCurrentMember"
                                    />
                                    <b-button
                                        variant="success"
                                        class="btn-icon"
                                        size="sm"
                                        :disabled="!newCurrentMember.trim()"
                                        @click="addCurrentMember"
                                    >
                                      <feather-icon icon="PlusIcon" />
                                    </b-button>
                                  </div>
                                </b-col>

                                <!-- Бывшие участники -->
                                <b-col md="6">
                                  <h6 class="mt-1">Бывшие участники</h6>
                                  <div
                                      v-for="(member, idx) in formerMembers"
                                      :key="'fmr-' + idx"
                                      class="d-flex align-items-center mb-50"
                                      style="gap: 0.25rem;"
                                  >
                                    <b-button
                                        variant="flat-secondary"
                                        class="btn-icon p-25"
                                        size="sm"
                                        :disabled="idx === 0"
                                        @click="moveMemberUp(formerMembers, idx)"
                                    >
                                      <feather-icon icon="ChevronUpIcon" size="14" />
                                    </b-button>
                                    <b-button
                                        variant="flat-secondary"
                                        class="btn-icon p-25"
                                        size="sm"
                                        :disabled="idx === formerMembers.length - 1"
                                        @click="moveMemberDown(formerMembers, idx)"
                                    >
                                      <feather-icon icon="ChevronDownIcon" size="14" />
                                    </b-button>
                                    <b-form-input
                                        v-model="formerMembers[idx]"
                                        size="sm"
                                        class="flex-grow-1"
                                    />
                                    <b-button
                                        variant="flat-info"
                                        class="btn-icon p-25"
                                        size="sm"
                                        v-b-tooltip.hover
                                        title="В текущие"
                                        @click="moveToCurrent(idx)"
                                    >
                                      <feather-icon icon="ArrowLeftIcon" size="14" />
                                    </b-button>
                                    <b-button
                                        variant="flat-danger"
                                        class="btn-icon p-25"
                                        size="sm"
                                        @click="removeMember(formerMembers, idx)"
                                    >
                                      <feather-icon icon="XIcon" size="14" />
                                    </b-button>
                                  </div>
                                  <div v-if="!formerMembers.length" class="text-muted mb-50">Нет бывших участников</div>
                                  <div class="d-flex align-items-center mt-50" style="gap: 0.25rem;">
                                    <b-form-input
                                        v-model="newFormerMember"
                                        size="sm"
                                        placeholder="Новый бывший участник"
                                        class="flex-grow-1"
                                        @keyup.enter="addFormerMember"
                                    />
                                    <b-button
                                        variant="success"
                                        class="btn-icon"
                                        size="sm"
                                        :disabled="!newFormerMember.trim()"
                                        @click="addFormerMember"
                                    >
                                      <feather-icon icon="PlusIcon" />
                                    </b-button>
                                  </div>
                                </b-col>
                              </b-row>
                            </div>
                          </b-tab>

                          <!-- Tab: Обложки -->
                          <b-tab @click="loadSavedCovers">
                            <template #title>
                              <feather-icon
                                  icon="ImageIcon"
                                  size="16"
                                  class="mr-0 mr-sm-50"
                              />
                              <span class="d-none d-sm-inline">Обложки</span>
                            </template>

                            <div class="my-1 py-25">
                              <!-- Сохранённые обложки -->
                              <div v-if="isLoadingSavedCovers" class="mb-1">
                                <b-spinner small class="mr-50" />
                                <span class="text-muted">Загрузка обложек...</span>
                              </div>
                              <div v-if="savedCovers.length" class="mb-2">
                                <h5>Сохранённые обложки</h5>
                                <div class="d-flex flex-wrap" style="gap: 1rem;">
                                  <div
                                      v-for="(cover, index) in savedCovers"
                                      :key="cover.id"
                                      style="width: 200px;"
                                  >
                                    <div class="position-relative">
                                      <b-img
                                          :src="cover.url"
                                          fluid
                                          rounded
                                          style="width: 200px; height: 200px; object-fit: cover; cursor: pointer;"
                                          @click="openFullCover(index)"
                                      />
                                      <b-button
                                          variant="danger"
                                          class="btn-icon position-absolute"
                                          size="sm"
                                          style="top: 8px; right: 8px; opacity: 0.9;"
                                          @click.stop="deleteCover(cover)"
                                          v-b-tooltip.hover
                                          title="Удалить обложку"
                                      >
                                        <feather-icon icon="TrashIcon" />
                                      </b-button>
                                    </div>
                                    <div class="mt-50">
                                      <strong v-if="cover.name" class="d-block font-small-3 text-truncate">{{ cover.name }}</strong>
                                      <b-form-rating
                                          :value="cover.difficulty || 0"
                                          :stars="5"
                                          inline
                                          no-border
                                          size="sm"
                                          variant="warning"
                                          class="p-0"
                                          @change="updateCoverDifficulty(cover, $event)"
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <b-button
                                  variant="success"
                                  class="mb-1"
                                  :disabled="isLoadingCovers || !hasSpotifyId"
                                  @click="searchSpotifyCovers"
                              >
                                <b-spinner v-if="isLoadingCovers" small class="mr-50" />
                                <feather-icon v-else icon="SearchIcon" class="mr-50" />
                                <span class="align-middle">Загрузить из Спотифай</span>
                              </b-button>

                              <div v-if="coversError" class="text-danger mb-1">{{ coversError }}</div>

                              <div v-if="spotifyCovers.length">
                                <template v-for="(group, gIndex) in groupedSpotifyCovers">
                                  <hr v-if="gIndex > 0" :key="'sep-' + gIndex" class="w-100 my-1" />
                                  <div :key="'grp-' + gIndex" class="d-flex flex-wrap" style="gap: 1rem;">
                                    <div
                                        v-for="(album, index) in group"
                                        :key="index"
                                        class="cover-card"
                                        style="width: 200px;"
                                        :class="{ 'cover-saved': isCoverSaved(album) }"
                                    >
                                  <div
                                      class="position-relative cursor-pointer"
                                      @click="openCoverPreview(album)"
                                  >
                                    <b-img
                                        :src="album.preview || album.cover"
                                        fluid
                                        rounded
                                        style="width: 200px; height: 200px; object-fit: cover;"
                                        :style="isCoverSaved(album) ? 'border: 3px solid #28c76f; box-sizing: border-box;' : ''"
                                    />
                                    <b-badge
                                        v-if="isCoverSaved(album)"
                                        variant="success"
                                        class="position-absolute"
                                        style="top: 8px; right: 8px; font-size: 0.85rem;"
                                        v-b-tooltip.hover
                                        title="Обложка сохранена"
                                    >
                                      <feather-icon icon="CheckIcon" size="14" />
                                    </b-badge>
                                    <b-badge
                                        v-if="album.total_tracks"
                                        variant="dark"
                                        class="position-absolute"
                                        style="top: 8px; left: 8px; opacity: 0.85; font-size: 0.85rem;"
                                        v-b-tooltip.hover
                                        title="Кол-во треков"
                                    >
                                      <feather-icon icon="MusicIcon" size="12" class="mr-25" />
                                      {{ album.total_tracks }}
                                    </b-badge>
                                    <b-button
                                        variant="success"
                                        class="btn-icon position-absolute"
                                        size="sm"
                                        style="bottom: 8px; right: 8px; opacity: 0.9;"
                                        @click.stop="saveCover(album)"
                                        v-b-tooltip.hover
                                        title="Сохранить обложку"
                                    >
                                      <feather-icon icon="DownloadIcon" />
                                    </b-button>
                                  </div>
                                  <div class="mt-50">
                                    <strong class="d-block font-small-3 text-truncate">{{ album.name }}</strong>
                                    <strong
                                        v-if="album.artists"
                                        class="d-block font-small-2 text-truncate text-primary"
                                    >{{ album.artists }}</strong>
                                    <div class="d-flex align-items-center" style="gap: 0.4rem;">
                                      <span v-if="album.release_date" class="text-muted font-small-2">{{ formatReleaseDate(album.release_date, album.release_date_precision) }}</span>
                                      <b-badge v-if="album.album_type" :variant="albumTypeVariant(album.album_type)" pill class="font-small-1">
                                        <feather-icon :icon="albumTypeIcon(album.album_type)" size="10" class="mr-25" />
                                        {{ albumTypeLabel(album.album_type) }}
                                      </b-badge>
                                    </div>
                                  </div>
                                    </div>
                                  </div>
                                </template>
                              </div>

                              <div v-else-if="coversSearched && !coversError" class="text-muted">
                                Обложки не найдены
                              </div>

                              <hr class="w-100 my-1" />

                              <b-button
                                  variant="danger"
                                  class="mb-1"
                                  :disabled="isLoadingYandexCovers || !hasYandexId"
                                  @click="searchYandexCovers"
                              >
                                <b-spinner v-if="isLoadingYandexCovers" small class="mr-50" />
                                <feather-icon v-else icon="SearchIcon" class="mr-50" />
                                <span class="align-middle">Загрузить из Яндекса</span>
                              </b-button>

                              <div v-if="yandexCoversError" class="text-danger mb-1">{{ yandexCoversError }}</div>

                              <div v-if="yandexCovers.length">
                                <template v-for="(group, gIndex) in groupedYandexCovers">
                                  <hr v-if="gIndex > 0" :key="'ya-sep-' + gIndex" class="w-100 my-1" />
                                  <div :key="'ya-grp-' + gIndex" class="d-flex flex-wrap" style="gap: 1rem;">
                                    <div
                                        v-for="(album, index) in group"
                                        :key="index"
                                        class="cover-card"
                                        style="width: 200px;"
                                        :class="{ 'cover-saved': isCoverSaved(album) }"
                                    >
                                  <div
                                      class="position-relative cursor-pointer"
                                      @click="openCoverPreview(album)"
                                  >
                                    <b-img
                                        :src="album.preview || album.cover"
                                        fluid
                                        rounded
                                        style="width: 200px; height: 200px; object-fit: cover;"
                                        :style="isCoverSaved(album) ? 'border: 3px solid #28c76f; box-sizing: border-box;' : ''"
                                    />
                                    <b-badge
                                        v-if="isCoverSaved(album)"
                                        variant="success"
                                        class="position-absolute"
                                        style="top: 8px; right: 8px; font-size: 0.85rem;"
                                        v-b-tooltip.hover
                                        title="Обложка сохранена"
                                    >
                                      <feather-icon icon="CheckIcon" size="14" />
                                    </b-badge>
                                    <b-badge
                                        v-if="album.total_tracks"
                                        variant="dark"
                                        class="position-absolute"
                                        style="top: 8px; left: 8px; opacity: 0.85; font-size: 0.85rem;"
                                        v-b-tooltip.hover
                                        title="Кол-во треков"
                                    >
                                      <feather-icon icon="MusicIcon" size="12" class="mr-25" />
                                      {{ album.total_tracks }}
                                    </b-badge>
                                    <b-button
                                        variant="success"
                                        class="btn-icon position-absolute"
                                        size="sm"
                                        style="bottom: 8px; right: 8px; opacity: 0.9;"
                                        @click.stop="saveCover(album)"
                                        v-b-tooltip.hover
                                        title="Сохранить обложку"
                                    >
                                      <feather-icon icon="DownloadIcon" />
                                    </b-button>
                                  </div>
                                  <div class="mt-50">
                                    <strong class="d-block font-small-3 text-truncate">{{ album.name }}</strong>
                                    <strong
                                        v-if="album.artists"
                                        class="d-block font-small-2 text-truncate text-primary"
                                    >{{ album.artists }}</strong>
                                    <div class="d-flex align-items-center" style="gap: 0.4rem;">
                                      <span v-if="album.release_date" class="text-muted font-small-2">{{ formatReleaseDate(album.release_date, album.release_date_precision) }}</span>
                                      <b-badge v-if="album.album_type" :variant="albumTypeVariant(album.album_type)" pill class="font-small-1">
                                        <feather-icon :icon="albumTypeIcon(album.album_type)" size="10" class="mr-25" />
                                        {{ albumTypeLabel(album.album_type) }}
                                      </b-badge>
                                    </div>
                                  </div>
                                    </div>
                                  </div>
                                </template>
                              </div>
                              <div v-else-if="yandexCoversSearched && !yandexCoversError" class="text-muted">
                                Обложки не найдены
                              </div>
                            </div>
                          </b-tab>

                          <!-- Tab: Фото -->
                          <b-tab @click="loadSavedPhotos">
                            <template #title>
                              <feather-icon icon="CameraIcon" size="16" class="mr-0 mr-sm-50" />
                              <span class="d-none d-sm-inline">Фото</span>
                              <b-badge pill variant="light-primary" class="ml-50">
                                {{ savedPhotosLoaded ? savedPhotos.length : (artistDetail.photos_count || 0) }}
                              </b-badge>
                            </template>

                            <div class="my-1 py-25">
                              <div v-if="isLoadingSavedPhotos" class="mb-1">
                                <b-spinner small class="mr-50" />
                                <span class="text-muted">Загрузка фото...</span>
                              </div>

                              <div v-if="savedPhotos.length" class="mb-2">
                                <h5>Сохранённые фото</h5>
                                <div class="d-flex flex-wrap" style="gap: 1rem;">
                                  <div v-for="photo in savedPhotos" :key="photo.id" style="width: 200px;">
                                    <div class="position-relative">
                                      <b-img
                                          :src="photo.url"
                                          fluid rounded
                                          style="width: 200px; height: 200px; object-fit: cover; cursor: pointer;"
                                          @click="openPhotoModal(photo, false)"
                                      />
                                      <b-button
                                          variant="danger"
                                          class="btn-icon position-absolute"
                                          size="sm"
                                          style="top: 8px; right: 8px; opacity: 0.9;"
                                          @click.stop="deletePhoto(photo)"
                                          v-b-tooltip.hover
                                          title="Удалить фото"
                                      >
                                        <feather-icon icon="TrashIcon" />
                                      </b-button>
                                    </div>
                                    <div class="mt-50">
                                      <b-badge v-if="photo.type" variant="light-primary" class="mb-25">{{ photoTypeLabel(photo.type) }}</b-badge>
                                      <strong v-if="photo.name" class="d-block font-small-3 text-truncate">{{ photo.name }}</strong>
                                      <span v-if="photo.width && photo.height" :class="isHighResPhoto(photo) ? 'text-success font-weight-bold font-small-2' : 'text-muted font-small-2'">{{ photo.width }}×{{ photo.height }}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div class="d-flex flex-wrap mb-1" style="gap: 0.5rem;">
                                <b-button
                                    variant="primary"
                                    :disabled="isLoadingPhotos"
                                    @click="searchPhotos(1, '', null)"
                                >
                                  <b-spinner v-if="isLoadingPhotos && activePhotoQuery === ''" small class="mr-50" />
                                  <feather-icon v-else icon="SearchIcon" class="mr-50" />
                                  <span class="align-middle">Поиск</span>
                                </b-button>
                                <b-button
                                    v-for="preset in photoQueryPresets"
                                    :key="preset.query"
                                    variant="outline-primary"
                                    :disabled="isLoadingPhotos"
                                    @click="searchPhotos(1, preset.query, preset.type)"
                                >
                                  <b-spinner v-if="isLoadingPhotos && activePhotoQuery === preset.query" small class="mr-50" />
                                  <feather-icon v-else icon="SearchIcon" class="mr-50" />
                                  <span class="align-middle">{{ preset.label }}</span>
                                </b-button>
                              </div>

                              <div v-if="photosError" class="text-danger mb-1">{{ photosError }}</div>

                              <div v-if="photoResults.length" class="d-flex flex-wrap" style="gap: 1rem;">
                                <div v-for="(photo, index) in photoResults" :key="photo.image_url" style="width: 200px;">
                                  <div class="position-relative cursor-pointer" @click="openPhotoModal(photo, true)">
                                    <b-img
                                        :src="photo.thumbnail || photo.image_url"
                                        fluid rounded
                                        style="width: 200px; height: 200px; object-fit: cover;"
                                        :style="isPhotoSaved(photo) ? 'border: 3px solid #28c76f; box-sizing: border-box;' : ''"
                                    />
                                    <b-badge
                                        v-if="isPhotoSaved(photo)"
                                        variant="success"
                                        class="position-absolute"
                                        style="top: 8px; right: 8px; font-size: 0.85rem;"
                                        v-b-tooltip.hover
                                        title="Уже сохранено"
                                    >
                                      <feather-icon icon="CheckIcon" size="14" />
                                    </b-badge>
                                    <b-badge
                                        v-if="photo.width && photo.height"
                                        :variant="isHighResPhoto(photo) ? 'success' : 'dark'"
                                        class="position-absolute"
                                        style="top: 8px; left: 8px; opacity: 0.85; font-size: 0.75rem;"
                                    >
                                      {{ photo.width }}×{{ photo.height }}
                                    </b-badge>
                                  </div>
                                  <div class="mt-50">
                                    <strong class="d-block font-small-3 text-truncate">{{ photo.title }}</strong>
                                    <a
                                        v-if="photo.source_url"
                                        :href="photo.source_url"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        class="font-small-2"
                                    >
                                      <feather-icon icon="ExternalLinkIcon" size="11" class="mr-25" />Источник
                                    </a>
                                  </div>
                                </div>
                              </div>

                              <div v-else-if="photosSearched && !photosError" class="text-muted">
                                Фото не найдены
                              </div>

                              <div v-if="photoNextStart" class="mt-1">
                                <b-button variant="outline-primary" :disabled="isLoadingPhotos" @click="loadMorePhotos">
                                  <b-spinner v-if="isLoadingPhotos" small class="mr-50" />
                                  <span class="align-middle">Показать ещё</span>
                                </b-button>
                              </div>
                            </div>
                          </b-tab>

                          <!-- Tab: Альбомы -->
                          <b-tab>
                            <template #title>
                              <feather-icon
                                  icon="DiscIcon"
                                  size="16"
                                  class="mr-0 mr-sm-50"
                              />
                              <span class="d-none d-sm-inline">Альбомы</span>
                            </template>

                            <div ref="albumsContainer" class="my-1 py-25">
                              <!-- Spotify -->
                              <b-button
                                  variant="success"
                                  class="mb-1"
                                  :disabled="isLoadingCovers || !hasSpotifyId"
                                  @click="searchSpotifyCovers"
                              >
                                <b-spinner v-if="isLoadingCovers" small class="mr-50" />
                                <feather-icon v-else icon="SearchIcon" class="mr-50" />
                                <span class="align-middle">Загрузить из Спотифай</span>
                              </b-button>

                              <div v-if="coversError" class="text-danger mb-1">{{ coversError }}</div>

                              <div v-if="spotifyCovers.length">
                                <template v-for="(group, gIndex) in groupedSpotifyCovers">
                                  <hr v-if="gIndex > 0" :key="'al-sp-sep-' + gIndex" class="w-100 my-1" />
                                  <template v-for="(row, rIndex) in albumRows(group)">
                                    <div :key="'al-sp-row-' + gIndex + '-' + rIndex" class="d-flex flex-wrap" style="gap: 1rem;">
                                      <album-card
                                          v-for="album in row"
                                          :key="'sp-' + album.id"
                                          :album="album"
                                          source="spotify"
                                          :active="!!activeAlbum && activeAlbum.source === 'spotify' && activeAlbum.id === album.id"
                                          @toggle="toggleAlbumTracks"
                                          @add-cover="openCoverPreview"
                                      />
                                    </div>
                                    <album-tracks-panel
                                        v-if="rowHasActiveAlbum(row, 'spotify')"
                                        :key="'al-sp-panel-' + gIndex + '-' + rIndex"
                                        :album-name="activeAlbum.name"
                                        :tracks="albumTracks[activeAlbum.id] || null"
                                        :loading="!!albumTracksLoading[activeAlbum.id]"
                                        @close="closeAlbumTracks"
                                        @add-track="addAlbumTrack"
                                    />
                                  </template>
                                </template>
                              </div>
                              <div v-else-if="coversSearched && !coversError" class="text-muted">
                                Альбомы не найдены
                              </div>

                              <hr class="w-100 my-1" />

                              <!-- Yandex -->
                              <b-button
                                  variant="danger"
                                  class="mb-1"
                                  :disabled="isLoadingYandexCovers || !hasYandexId"
                                  @click="searchYandexCovers"
                              >
                                <b-spinner v-if="isLoadingYandexCovers" small class="mr-50" />
                                <feather-icon v-else icon="SearchIcon" class="mr-50" />
                                <span class="align-middle">Загрузить из Яндекса</span>
                              </b-button>

                              <div v-if="yandexCoversError" class="text-danger mb-1">{{ yandexCoversError }}</div>

                              <div v-if="yandexCovers.length">
                                <template v-for="(group, gIndex) in groupedYandexCovers">
                                  <hr v-if="gIndex > 0" :key="'al-ya-sep-' + gIndex" class="w-100 my-1" />
                                  <template v-for="(row, rIndex) in albumRows(group)">
                                    <div :key="'al-ya-row-' + gIndex + '-' + rIndex" class="d-flex flex-wrap" style="gap: 1rem;">
                                      <album-card
                                          v-for="album in row"
                                          :key="'ya-' + album.id"
                                          :album="album"
                                          source="yandex"
                                          :active="!!activeAlbum && activeAlbum.source === 'yandex' && activeAlbum.id === album.id"
                                          @toggle="toggleAlbumTracks"
                                          @add-cover="openCoverPreview"
                                      />
                                    </div>
                                    <album-tracks-panel
                                        v-if="rowHasActiveAlbum(row, 'yandex')"
                                        :key="'al-ya-panel-' + gIndex + '-' + rIndex"
                                        :album-name="activeAlbum.name"
                                        :tracks="albumTracks[activeAlbum.id] || null"
                                        :loading="!!albumTracksLoading[activeAlbum.id]"
                                        @close="closeAlbumTracks"
                                        @add-track="addAlbumTrack"
                                    />
                                  </template>
                                </template>
                              </div>
                              <div v-else-if="yandexCoversSearched && !yandexCoversError" class="text-muted">
                                Альбомы не найдены
                              </div>
                            </div>
                          </b-tab>
                        </b-tabs>
                      </b-col>
                    </b-row>
                  </b-overlay>
                </b-card>
              </b-col>
            </b-row>

            <div
                class="blog-content"
                v-html="artistDetail.description"
            />

          </b-card>
        </b-col>
        <!--/ blogs -->
      </b-row>
      <!--/ blogs -->
    </div>
    <!--/ content -->

    <!-- sidebar -->
    <div
        slot="sidebar"
        class="blog-sidebar py-2 py-lg-0"
    >
      <!-- Похожие артисты -->
      <div class="blog-recent-posts mt-3" v-if="similar">
        <h6 class="section-label mb-75">
          Похожие артисты
        </h6>
        <b-media
            v-for="(artist, index) in similar"
            :key="artist.mbid"
            no-body
            :class="index? 'mt-2':''"
        >
          <b-media-body>
            <h6 class="blog-recent-post-title">
              <b-link class="text-body-heading">
                <feather-icon
                    icon="CompassIcon"
                    class="mr-25"
                />
                {{ artist.name }}
              </b-link>
            </h6>

            <div class="text-nowrap">
              <b-button
                  v-if="!artist.db"
                  variant="flat-success"
                  class="btn-icon"
                  size="sm"
                  @click="addArtist(artist)"
                  :disabled="artist.loading"
              >
                <feather-icon icon="UserPlusIcon"/>
              </b-button>
              <b-button
                  v-else
                  :to="{name: 'artist-view', params: { id: artist.db.id }}"
                  variant="primary"
                  class="btn-icon"
                  size="sm"
              >
                <feather-icon icon="ArrowRightCircleIcon"/>
              </b-button>
              <b-button
                  :href="artist.url"
                  variant="flat-warning"
                  class="btn-icon ml-1"
                  size="sm"
              >
                <feather-icon icon="CastIcon"/>
              </b-button>
              <b-button
                  v-if="!artist.db"
                  :to="{name: 'artists-search', query: { q: artist.name }}"
                  variant="flat-primary"
                  class="btn-icon ml-1"
                  size="sm"
              >
                <feather-icon icon="SearchIcon"/>
              </b-button>
            </div>
          </b-media-body>
        </b-media>
      </div>
      <!--/ Похожие артисты -->
    </div>

    <!-- SHS Candidates Modal -->
    <b-modal
        v-model="showShsCandidates"
        title="Выберите артиста в SHS"
        hide-footer
        centered
    >
      <div v-if="shsCandidates.length">
        <div
            v-for="candidate in shsCandidates"
            :key="candidate.shs_id"
            class="d-flex align-items-center mb-1"
        >
          <b-button
              variant="primary"
              size="sm"
              class="mr-1"
              @click="selectShsCandidate(candidate.shs_id)"
          >
            <feather-icon icon="CheckIcon"/>
          </b-button>
          <span>{{ candidate.name }}</span>
          <a :href="candidate.uri" target="_blank" class="ml-auto">
            <feather-icon icon="ExternalLinkIcon"/>
          </a>
        </div>
      </div>
      <div v-else class="text-muted">Артист не найден в SecondHandSongs</div>
    </b-modal>

    <!-- Link SHS Track Modal -->
    <b-modal
        v-model="showLinkShsTrackModal"
        title="Привязать трек из SHS"
        centered
        ok-title="Привязать"
        cancel-title="Отмена"
        :ok-disabled="!selectedTrackIdForLink"
        @ok="linkShsTrack"
    >
      <div v-if="linkShsTrackItem">
        <p class="mb-50"><strong>SHS трек:</strong> {{ linkShsTrackItem.shs_title || linkShsTrackItem.title }}</p>
        <label class="mb-50">Выберите трек из БД:</label>
        <v-select
            v-model="selectedTrackIdForLink"
            :options="artistTracksForLink"
            label="label"
            :reduce="val => val.value"
            placeholder="Поиск трека..."
            :filterable="true"
            :append-to-body="true"
        />
      </div>
    </b-modal>

    <!-- Cover Preview Modal -->
    <b-modal
        v-model="showCoverPreview"
        :title="previewAlbum ? previewAlbum.name : ''"
        hide-footer
        centered
        size="lg"
    >
      <cover-inpainting
          v-if="previewAlbum"
          :album="previewAlbum"
          @save="saveCoverWithUrl"
      />
    </b-modal>

    <!-- Full-size Saved Cover Modal -->
    <b-modal
        v-model="showFullCover"
        :title="currentFullCover && currentFullCover.name ? currentFullCover.name : 'Обложка'"
        hide-footer
        centered
        size="xl"
        @shown="onFullCoverShown"
        @hidden="onFullCoverHidden"
    >
      <div v-if="currentFullCover" class="d-flex align-items-center" style="gap: 1rem;">
        <b-button
            variant="flat-secondary"
            class="btn-icon"
            :disabled="fullCoverIndex === 0"
            v-b-tooltip.hover
            title="Предыдущая (←)"
            @click="goPrevCover"
        >
          <feather-icon icon="ChevronLeftIcon" size="32" />
        </b-button>

        <div class="flex-grow-1 text-center">
          <b-img
              :src="currentFullCover.url"
              fluid
              style="max-width: 100%; max-height: 80vh; object-fit: contain;"
          />
        </div>

        <b-button
            variant="flat-secondary"
            class="btn-icon"
            :disabled="fullCoverIndex >= savedCovers.length - 1"
            v-b-tooltip.hover
            title="Следующая (→)"
            @click="goNextCover"
        >
          <feather-icon icon="ChevronRightIcon" size="32" />
        </b-button>
      </div>

      <div v-if="currentFullCover" class="d-flex align-items-center justify-content-between mt-1">
        <b-form-rating
            :value="currentFullCover.difficulty || 0"
            :stars="5"
            inline
            no-border
            size="lg"
            variant="warning"
            class="p-0"
            @change="updateCoverDifficulty(currentFullCover, $event)"
        />
        <div class="text-muted">
          {{ fullCoverIndex + 1 }} / {{ savedCovers.length }}
        </div>
        <b-button
            variant="danger"
            @click="deleteCurrentCover"
        >
          <feather-icon icon="TrashIcon" class="mr-50" />
          Удалить
        </b-button>
      </div>
    </b-modal>

    <!-- Photo Modal -->
    <b-modal
        v-model="showPhotoModal"
        :title="currentPhoto && (currentPhoto.title || currentPhoto.name) ? (currentPhoto.title || currentPhoto.name) : 'Фото'"
        hide-footer
        centered
        size="xl"
    >
      <div v-if="currentPhoto" class="text-center">
        <b-img
            :src="currentPhoto.isResult ? currentPhoto.image_url : currentPhoto.url"
            fluid
            style="max-width: 100%; max-height: 80vh; object-fit: contain;"
        />
      </div>
      <div v-if="currentPhoto" class="d-flex align-items-center justify-content-between mt-1" style="gap: 1rem;">
        <span v-if="currentPhoto.width && currentPhoto.height" :class="isHighResPhoto(currentPhoto) ? 'text-success font-weight-bold' : 'text-muted'">
          {{ currentPhoto.width }}×{{ currentPhoto.height }}
        </span>
        <a
            v-if="currentPhoto.isResult && currentPhoto.source_url"
            :href="currentPhoto.source_url"
            target="_blank"
            rel="noopener noreferrer"
        >
          <feather-icon icon="ExternalLinkIcon" class="mr-50" />Источник
        </a>
        <span v-if="savePhotoError" class="text-danger font-small-3 ml-auto mr-1">{{ savePhotoError }}</span>
        <b-button
            v-if="currentPhoto.isResult"
            variant="success"
            :disabled="isSavingPhoto || isPhotoSaved(currentPhoto)"
            @click="savePhoto(currentPhoto)"
        >
          <b-spinner v-if="isSavingPhoto" small class="mr-50" />
          <feather-icon v-else :icon="isPhotoSaved(currentPhoto) ? 'CheckIcon' : 'DownloadIcon'" class="mr-50" />
          {{ isPhotoSaved(currentPhoto) ? 'Уже сохранено' : 'Сохранить' }}
        </b-button>
        <b-button
            v-if="currentPhoto.isResult && savePhotoError && !isPhotoSaved(currentPhoto)"
            variant="outline-primary"
            :disabled="isSavingPhoto"
            @click="savePhotoViaProxy(currentPhoto)"
            v-b-tooltip.hover
            title="Сервер сам скачает картинку с браузерными заголовками (обходит hotlink-защиту)"
        >
          <feather-icon icon="ServerIcon" class="mr-50" />
          Через сервер
        </b-button>
        <b-button
            v-if="currentPhoto.isResult && savePhotoError && !isPhotoSaved(currentPhoto)"
            variant="outline-warning"
            :disabled="isSavingPhoto"
            @click="savePhotoViaBrowser(currentPhoto)"
            v-b-tooltip.hover
            title="Браузер скачает уже отображаемую картинку и загрузит файлом (нужен CORS у источника)"
        >
          <feather-icon icon="GlobeIcon" class="mr-50" />
          Через браузер
        </b-button>
        <b-button
            v-if="!currentPhoto.isResult"
            variant="danger"
            @click="deletePhoto(currentPhoto)"
        >
          <feather-icon icon="TrashIcon" class="mr-50" />
          Удалить
        </b-button>
      </div>
    </b-modal>

  </content-with-sidebar>
</template>

<script>
import {
  BFormInput,
  BMedia,
  BAvatar,
  BMediaAside,
  BMediaBody,
  BImg,
  BLink,
  BFormGroup,
  BInputGroup,
  BInputGroupAppend,
  BCard,
  BRow,
  BCol,
  BBadge,
  BCardText,
  BDropdown,
  BDropdownItem,
  BForm,
  BFormTextarea,
  BFormCheckbox,
  BButton,
  BTab,
  BTabs,
  BSpinner,
  BOverlay,
  BTable,
  BFormRating,
  BModal,
  VBTooltip,
} from 'bootstrap-vue'
import AlbumCard from './AlbumCard.vue'
import AlbumTracksPanel from './AlbumTracksPanel.vue'
import Ripple from 'vue-ripple-directive'
import {badWord, formatNumber, formatDate} from '@core/utils/filter'
import ContentWithSidebar from '@core/layouts/components/content-with-sidebar/ContentWithSidebar.vue'
import router from "@/router";
import store from "@/store";
import musicStoreModule from "@/views/db-music/musicStoreModule";
import {onMounted, onUnmounted, ref, computed, watch, nextTick} from "@vue/composition-api";
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import TrackRow from "@/views/db-music/TrackRow";
import CoverInpainting from "@/views/db-music/CoverInpainting";
import vSelect from "vue-select";
import {count} from "echarts/src/component/dataZoom/history";

export default {
  components: {
    TrackRow,
    CoverInpainting,
    AlbumCard,
    AlbumTracksPanel,
    BFormInput,
    BMedia,
    BAvatar,
    BMediaAside,
    BMediaBody,
    BLink,
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BInputGroup,
    BInputGroupAppend,
    BImg,
    BBadge,
    BCardText,
    BDropdown,
    BForm,
    BDropdownItem,
    BFormTextarea,
    BFormCheckbox,
    BButton,
    BTab,
    BTabs,
    BSpinner,
    BOverlay,
    BTable,
    BFormRating,
    BModal,
    vSelect,
    ContentWithSidebar,
  },
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },
  data() {
    return {

    }
  },
  methods: {
    count,
    rowClass(item, type) {
      const colorClass = 'table-danger'
      if (!item || type !== 'row') { return }

      // eslint-disable-next-line consistent-return
      if (badWord(item.title)) { return colorClass }
    },
  },
  setup() {
    const STORE_MODULE_NAME = 'artists'
    const toast = useToast()

    // Register module
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, musicStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const artistDetail = ref(null)
    const similar = ref([])
    const additional = ref([])
    const trackSearchQuery = ref('')
    const artistAliases = ref([])
    const aliasSearchResults = ref([])
    const manualAliasName = ref('')
    const shsTracks = ref([])
    const shsTracksError = ref('')
    const isEditingTitle = ref(false)
    const editTitle = ref('')
    const isLoadingArtist = ref(false)
    const isLoadingStaff = ref(false)
    const disabledArtistId = ref(null)
    const showShsCandidates = ref(false)
    const shsCandidates = ref([])
    const showLinkShsTrackModal = ref(false)
    const linkShsTrackItem = ref(null)
    const selectedTrackIdForLink = ref(null)

    const savedCovers = ref([])
    const isLoadingSavedCovers = ref(false)
    const savedCoversLoaded = ref(false)

    const savedPhotos = ref([])
    const isLoadingSavedPhotos = ref(false)
    const savedPhotosLoaded = ref(false)
    const photoResults = ref([])
    const isLoadingPhotos = ref(false)
    const photosSearched = ref(false)
    const photosError = ref('')
    const photoNextStart = ref(null)
    const showPhotoModal = ref(false)
    const currentPhoto = ref(null)
    const isSavingPhoto = ref(false)
    const savePhotoError = ref('')
    const activePhotoQuery = ref('')
    const activePhotoType = ref(null)
    // Пресеты: query — суффикс к имени артиста для Google, type — сохраняемый тип фото (enum). Обычный поиск: query='' / type=null.
    const photoQueryPresets = [
      {label: 'Фотосессия', query: 'фотосессия', type: 'photoshoot'},
      {label: 'Концерт', query: 'концерт', type: 'live'},
      {label: 'Портрет', query: 'портрет', type: 'portrait'},
      {label: 'В молодости', query: 'в молодости', type: 'young'},
      {label: 'Постер', query: 'постер', type: 'poster'},
    ]
    const photoTypeLabel = (type) => {
      const preset = photoQueryPresets.find(p => p.type === type)
      return preset ? preset.label : type
    }

    const spotifyCovers = ref([])
    const isLoadingCovers = ref(false)
    const coversError = ref('')
    const coversSearched = ref(false)
    const yandexCovers = ref([])
    const isLoadingYandexCovers = ref(false)
    const yandexCoversError = ref('')
    const yandexCoversSearched = ref(false)
    const showCoverPreview = ref(false)
    const previewAlbum = ref(null)
    const showFullCover = ref(false)
    const fullCoverIndex = ref(0)

    // --- Альбомы (вкладка с раскрытием треков) ---
    // Один активный альбом одновременно: { source, id, name } либо null.
    const activeAlbum = ref(null)
    const albumTracks = ref({})
    const albumTracksLoading = ref({})
    // Ширина сетки обложек и число колонок — чтобы вставлять панель треков
    // под строку с активным альбомом (карточки во flex-wrap).
    const albumsContainer = ref(null)
    const albumColumns = ref(1)

    // --- Состав ---
    const currentMembers = ref([])
    const formerMembers = ref([])
    const isLoadingMembers = ref(false)
    const isSavingMembers = ref(false)
    const newCurrentMember = ref('')
    const newFormerMember = ref('')

    const hasSpotifyId = computed(() => {
      if (!artistDetail.value) return false
      const ext = artistDetail.value.external_ids
      return ext && !!ext.spotify
    })

    const groupedSpotifyCovers = computed(() => {
      const albums = []
      const singles = []
      const collabs = []
      spotifyCovers.value.forEach(album => {
        if (album.artists) {
          collabs.push(album)
        } else if (album.album_type === 'album') {
          albums.push(album)
        } else {
          singles.push(album)
        }
      })
      return [albums, singles, collabs].filter(g => g.length)
    })

    const hasYandexId = computed(() => {
      if (!artistDetail.value) return false
      const ext = artistDetail.value.external_ids
      return ext && !!ext.yandex
    })

    const groupedYandexCovers = computed(() => {
      const albums = []
      const singles = []
      const collabs = []
      yandexCovers.value.forEach(album => {
        if (album.artists) {
          collabs.push(album)
        } else if (album.album_type === 'album') {
          albums.push(album)
        } else {
          singles.push(album)
        }
      })
      return [albums, singles, collabs].filter(g => g.length)
    })

    const showShsSyncButton = computed(() => {
      if (!artistDetail.value) return false
      const ext = artistDetail.value.external_ids
      if (!ext || !('second_hand_songs' in ext)) return true
      if (ext.second_hand_songs === null) return true
      return false
    })

    const shsSyncDisabled = computed(() => {
      if (!artistDetail.value) return true
      const ext = artistDetail.value.external_ids
      if (!ext || !('second_hand_songs' in ext)) return false
      return ext.second_hand_songs === null
    })

    const showShsTracksButton = computed(() => {
      if (!artistDetail.value) return false
      const ext = artistDetail.value.external_ids
      return ext && 'second_hand_songs' in ext && ext.second_hand_songs != null
    })

    const shsTableColumns = [
      { key: 'performer', label: 'Исполнитель', sortable: true },
      { key: 'db_title', label: 'Треки в БД', sortable: true },
      { key: 'shs_title', label: 'Треки в SHS', sortable: true },
      { key: 'is_original', label: 'Тип', sortable: true },
      { key: 'actions', label: '', sortable: false },
      { key: 'uri', label: 'SHS', sortable: false },
    ]

    const mergedShsTracks = computed(() => {
      if (!artistDetail.value) return []
      const dbTracks = artistDetail.value.tracks || []
      const shsList = shsTracks.value || []

      // Set of DB track IDs that are linked to SHS tracks
      const linkedDbIds = new Set(shsList.filter(s => s.track_id).map(s => s.track_id))

      const rows = []

      // SHS tracks (linked and unlinked)
      shsList.forEach(s => {
        const linked = !!s.track_id
        const dbTrack = linked ? dbTracks.find(t => t.id === s.track_id) : null
        rows.push({
          _sortKey: (s.title || '').toLowerCase(),
          performer: s.performer || '',
          db_title: linked && dbTrack ? (dbTrack.track || dbTrack.title) : '',
          shs_title: s.title || '',
          is_original: s.is_original,
          date: s.date,
          track_id: s.track_id,
          shs_id: s.shs_id,
          uri: s.uri,
          linked,
          source: linked ? 'both' : 'shs',
          _rowVariant: linked ? 'success' : '',
        })
      })

      // DB tracks not linked to any SHS track
      dbTracks.forEach(t => {
        if (!linkedDbIds.has(t.id)) {
          rows.push({
            _sortKey: (t.track || t.title || '').toLowerCase(),
            performer: '',
            db_title: t.track || t.title || '',
            shs_title: '',
            is_original: null,
            date: null,
            track_id: t.id,
            shs_id: null,
            uri: null,
            linked: false,
            source: 'db',
            _rowVariant: '',
          })
        }
      })

      rows.sort((a, b) => a._sortKey.localeCompare(b._sortKey))
      return rows
    })

    // Table Handlers
    const tableColumns = [
      { key: 'id', label: '#', sortable: false },
      { key: 'artist', sortable: true },
      { key: 'track', sortable: true },
      { key: 'release_year', label: 'Год', sortable: true },
      { key: 'play_count', sortable: true },
      { key: 'info', label: 'Инфо', sortable: false },
      { key: 'covers', label: 'Каверы', sortable: false },
      { key: 'actions' },
    ]

    const fetchArtistData = () => {
      let id = router.currentRoute.params.id
      store.dispatch('artists/fetchArtistDetails', {
        id,
        queryParams:
            {
              with_tracks: 1,
              with_lists: 1,
            }
      }).then(res => {
        artistDetail.value = res.data.data
      })
    }

    const addArtist = async (artist) => {
      artist.loading = true
      blockData()
      try {
        await store.dispatch(`artists/createArtist`, {title: artist.name})
        fetchSimilar()
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error adding artist',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const deleteTrack = async (id) => {
      blockData()
      try {
        await store.dispatch('artists/deleteTrack', {id : id})
            .then(() => {
              refreshData()
            }).finally(() => { unblockData() })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error deleting track',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const loadLastfmInfo = async () => {
      let id = router.currentRoute.params.id
      blockData()
      try {
        await store.dispatch('artists/loadLastfmInfo', {id : id})
            .then(() => {
              refreshData()
            }).finally(() => { unblockData() })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error loading LastFM info',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const flushCache = async () => {
      let id = router.currentRoute.params.id
      blockData()
      try {
        const response = await store.dispatch('artists/flushCache', {id: id})
        toast({
          component: ToastificationContent,
          props: {
            title: `Кеш очищен (${response.data.deleted || 0} ключей)`,
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error flushing cache',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const loadAdditionalTracks = async () => {
      let id = router.currentRoute.params.id
      blockData()
      try {
        const response = await store.dispatch('artists/additionalTracks', {id : id})
        additional.value = response.data
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error loading additional tracks',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const searchTracksByName = async () => {
      if (!trackSearchQuery.value.trim()) return
      let id = router.currentRoute.params.id
      blockData()
      try {
        const response = await store.dispatch('artists/searchTracksByName', {
          id: id,
          queryParams: { query: trackSearchQuery.value.trim() }
        })
        additional.value = response.data
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error searching tracks',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const addTrack = async (title) => {
      let id = router.currentRoute.params.id
      blockData()
      try {
        await store.dispatch('artists/addTrack', {id : id, queryParams: {title: title}})
        refreshData()
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error adding track',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const toggleIgnore = async (track) => {
      blockData()
      try {
        await store.dispatch('artists/updateTrack', {id : track.id, queryParams: {ignore: !track.ignore}})
        refreshData()
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error updating track',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const downloadTrack = async (id) => {
      blockData()
      try {
        await store.dispatch('artist/downloadTrack', {id : id})
        refreshData()
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error downloading track',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const fetchSimilar = () => {
      let id = router.currentRoute.params.id
      store.dispatch('artists/fetchSimilarArtists', {id})
          .then(response => {
            similar.value = response.data
          })
    }

    const refreshData = async () => {
      blockData()
      try {
        await fetchArtistData()
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error refreshing artist data',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const blockData = () => {
      isLoadingArtist.value = true
    }

    const unblockData = () => {
      isLoadingArtist.value = false
    }

    const toStarRating = (popularity) => Math.round(popularity / 10) / 2

    const aliasesText = computed(() => {
      if (!artistAliases.value || !artistAliases.value.length) return ''
      return artistAliases.value.map(a => a.alias).join(', ')
    })

    fetchArtistData()
    fetchSimilar()

    onMounted(() => {
      fetchAliases()
    })

    const ignoreTracks = async () => {
      blockData()
      try {
        await store.dispatch('artists/ignoreTracks', {
          id: router.currentRoute.params.id
        })
        fetchArtistData()
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error ignoring tracks',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const wikipediaUrl = (value) => {
      if (!value) return '#'
      if (/^https?:\/\//i.test(value)) return value
      const match = value.match(/^([a-z]{2,3})[:/](.+)$/i)
      if (match) return `https://${match[1]}.wikipedia.org/wiki/${match[2]}`
      return `https://ru.wikipedia.org/wiki/${value}`
    }

    const wikipediaLabel = (value) => {
      if (!value) return ''
      const decode = (s) => {
        try { return decodeURIComponent(s) } catch (e) { return s }
      }
      if (/^https?:\/\//i.test(value)) {
        try {
          const url = new URL(value)
          const langMatch = url.hostname.match(/^([a-z]{2,3})\.wikipedia\.org$/i)
          const pathMatch = url.pathname.match(/^\/wiki\/(.+)$/)
          const lang = langMatch ? langMatch[1].toUpperCase() : ''
          const title = pathMatch ? decode(pathMatch[1]) : decode(url.pathname.replace(/^\//, ''))
          return lang ? `[${lang}]: ${title}` : title
        } catch (e) {
          return value
        }
      }
      const m = value.match(/^([a-z]{2,3})[:/](.+)$/i)
      if (m) return `[${m[1].toUpperCase()}]: ${decode(m[2])}`
      return decode(value)
    }

    const setExternalId = (key, value) => {
      if (!artistDetail.value.external_ids) {
        artistDetail.value.external_ids = {}
      }
      artistDetail.value = {
        ...artistDetail.value,
        external_ids: {
          ...artistDetail.value.external_ids,
          [key]: value,
        },
      }
    }

    const syncSpotify = async () => {
      let id = router.currentRoute.params.id
      blockData()
      try {
        const response = await store.dispatch('artists/syncArtistSpotify', {id})
        if (response.data.success) {
          if (response.data.spotify_id) {
            setExternalId('spotify', response.data.spotify_id)
          }
          toast({
            component: ToastificationContent,
            props: {
              title: response.data.message || 'Spotify синхронизирован',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        } else {
          toast({
            component: ToastificationContent,
            props: {
              title: response.data.message || 'Не найдено в Spotify',
              icon: 'AlertTriangleIcon',
              variant: 'warning',
            },
          })
        }
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка синхронизации с Spotify',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const syncYandex = async () => {
      let id = router.currentRoute.params.id
      blockData()
      try {
        const response = await store.dispatch('artists/syncArtistYandex', {id})
        if (response.data.success) {
          if (response.data.yandex_id) {
            setExternalId('yandex', response.data.yandex_id)
          }
          toast({
            component: ToastificationContent,
            props: {
              title: response.data.message || 'Yandex синхронизирован',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        } else {
          toast({
            component: ToastificationContent,
            props: {
              title: response.data.message || 'Не найдено в Yandex Music',
              icon: 'AlertTriangleIcon',
              variant: 'warning',
            },
          })
        }
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка синхронизации с Yandex Music',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const syncWikipedia = async () => {
      const id = router.currentRoute.params.id
      blockData()
      try {
        const response = await store.dispatch('artists/syncArtistWikipedia', {id})
        if (response.data.success) {
          if (response.data.url) {
            setExternalId('wikipedia', response.data.url)
          } else if (response.data.external_ids) {
            artistDetail.value = {
              ...artistDetail.value,
              external_ids: response.data.external_ids,
            }
          }
          toast({
            component: ToastificationContent,
            props: {
              title: response.data.message || 'Wikipedia синхронизирован',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        } else {
          toast({
            component: ToastificationContent,
            props: {
              title: response.data.message || 'Не найдено в Wikipedia',
              icon: 'AlertTriangleIcon',
              variant: 'warning',
            },
          })
        }
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка синхронизации с Wikipedia',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const syncShs = async () => {
      let id = router.currentRoute.params.id
      blockData()
      try {
        const response = await store.dispatch('artists/syncArtistShs', {id})
        if (response.data.success) {
          if (response.data.shs_id) {
            setExternalId('second_hand_songs', response.data.shs_id)
          }
          toast({
            component: ToastificationContent,
            props: {
              title: `Привязано: ${response.data.shs_name}`,
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        } else if (response.data.candidates && response.data.candidates.length) {
          shsCandidates.value = response.data.candidates
          showShsCandidates.value = true
        } else {
          toast({
            component: ToastificationContent,
            props: {
              title: response.data.message || 'Не найдено в SHS',
              icon: 'AlertTriangleIcon',
              variant: 'warning',
            },
          })
        }
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка синхронизации с SHS',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const selectShsCandidate = async (shsId) => {
      let id = router.currentRoute.params.id
      blockData()
      showShsCandidates.value = false
      try {
        const response = await store.dispatch('artists/syncArtistShs', {id, queryParams: {shs_id: shsId}})
        if (response.data.success) {
          setExternalId('second_hand_songs', shsId)
          toast({
            component: ToastificationContent,
            props: {
              title: response.data.message || 'SHS ID сохранён',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        } else {
          toast({
            component: ToastificationContent,
            props: {
              title: response.data.message || 'Ошибка сохранения',
              icon: 'AlertTriangleIcon',
              variant: 'warning',
            },
          })
        }
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка сохранения SHS ID',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    // --- Добавление / удаление внешних ID артиста ---
    const showAddExternalIdModal = ref(false)
    const externalIdInput = ref('')
    const isAddingExternalId = ref(false)

    const addExternalId = async () => {
      const url = externalIdInput.value.trim()
      if (!url) return
      isAddingExternalId.value = true
      try {
        const id = router.currentRoute.params.id
        const res = await store.dispatch('artists/addArtistExternalId', {id, url})
        showAddExternalIdModal.value = false
        externalIdInput.value = ''
        artistDetail.value.external_ids = res.data.external_ids
        toast({
          component: ToastificationContent,
          props: {
            title: 'Внешний ID добавлен',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        const message = error.response?.data?.message || 'Ошибка добавления внешнего ID'
        toast({
          component: ToastificationContent,
          props: {
            title: message,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isAddingExternalId.value = false
      }
    }

    const deleteExternalId = async (source) => {
      const id = router.currentRoute.params.id
      blockData()
      try {
        const res = await store.dispatch('artists/deleteArtistExternalId', {id, source})
        artistDetail.value.external_ids = res.data.external_ids
        toast({
          component: ToastificationContent,
          props: {
            title: 'Внешний ID удалён',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        const message = error.response?.data?.message || 'Ошибка удаления внешнего ID'
        toast({
          component: ToastificationContent,
          props: {
            title: message,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    // --- Состав: computed и методы ---
    const hasMembers = computed(() => {
      if (!artistDetail.value) return false
      return !!artistDetail.value.members
    })

    const initMembers = () => {
      if (artistDetail.value?.members) {
        const m = artistDetail.value.members
        currentMembers.value = m.current_members
          ? m.current_members.split('\n').filter(s => s.trim())
          : []
        formerMembers.value = m.former_members
          ? m.former_members.split('\n').filter(s => s.trim())
          : []
      } else {
        currentMembers.value = []
        formerMembers.value = []
      }
    }

    const loadMembers = async () => {
      const id = router.currentRoute.params.id
      isLoadingMembers.value = true
      try {
        const res = await store.dispatch('artists/loadArtistMembers', { id })
        if (res.data.success) {
          artistDetail.value = {
            ...artistDetail.value,
            members: res.data.data,
          }
          initMembers()
          try {
            const artistRes = await store.dispatch('artists/fetchArtistDetails', { id, queryParams: {} })
            if (artistRes.data.data && artistRes.data.data.external_ids) {
              artistDetail.value = {
                ...artistDetail.value,
                external_ids: artistRes.data.data.external_ids,
              }
            }
          } catch (e) {}
          toast({
            component: ToastificationContent,
            props: {
              title: 'Состав загружен',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        } else {
          toast({
            component: ToastificationContent,
            props: {
              title: res.data.message || 'Ошибка загрузки состава',
              icon: 'AlertTriangleIcon',
              variant: 'warning',
            },
          })
        }
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка загрузки состава',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoadingMembers.value = false
      }
    }

    const saveMembers = async () => {
      const id = router.currentRoute.params.id
      isSavingMembers.value = true
      try {
        const res = await store.dispatch('artists/updateArtistMembers', {
          id,
          currentMembers: currentMembers.value.filter(s => s.trim()).join('\n'),
          formerMembers: formerMembers.value.filter(s => s.trim()).join('\n'),
        })
        if (res.data.success) {
          artistDetail.value = {
            ...artistDetail.value,
            members: res.data.data,
            ...(res.data.data && res.data.data.external_ids ? { external_ids: res.data.data.external_ids } : {}),
          }
          initMembers()
          toast({
            component: ToastificationContent,
            props: {
              title: 'Состав сохранён',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        }
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка сохранения состава',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isSavingMembers.value = false
      }
    }

    const removeMember = (list, index) => {
      list.splice(index, 1)
    }

    const moveToFormer = (index) => {
      const member = currentMembers.value.splice(index, 1)[0]
      formerMembers.value.push(member)
    }

    const moveToCurrent = (index) => {
      const member = formerMembers.value.splice(index, 1)[0]
      currentMembers.value.push(member)
    }

    const addCurrentMember = () => {
      const name = newCurrentMember.value.trim()
      if (!name) return
      currentMembers.value.push(name)
      newCurrentMember.value = ''
    }

    const addFormerMember = () => {
      const name = newFormerMember.value.trim()
      if (!name) return
      formerMembers.value.push(name)
      newFormerMember.value = ''
    }

    const moveMemberUp = (list, index) => {
      if (index <= 0) return
      const tmp = list[index]
      list.splice(index, 1, list[index - 1])
      list.splice(index - 1, 1, tmp)
    }

    const moveMemberDown = (list, index) => {
      if (index >= list.length - 1) return
      const tmp = list[index]
      list.splice(index, 1, list[index + 1])
      list.splice(index + 1, 1, tmp)
    }

    const syncShsTracks = async () => {
      let id = router.currentRoute.params.id
      blockData()
      try {
        const response = await store.dispatch('artists/syncArtistShsTracks', {id})
        if (response.data.success) {
          toast({
            component: ToastificationContent,
            props: {
              title: `Привязано: ${response.data.linked}, не найдено: ${response.data.not_found}`,
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
          await fetchShsTracks()
        } else {
          toast({
            component: ToastificationContent,
            props: {
              title: response.data.message || 'Ошибка синхронизации треков',
              icon: 'AlertTriangleIcon',
              variant: 'warning',
            },
          })
        }
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка синхронизации треков SHS',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const fetchShsTracks = async () => {
      let id = router.currentRoute.params.id
      blockData()
      shsTracksError.value = ''
      try {
        const response = await store.dispatch('artists/fetchArtistShsTracks', {id})
        if (response.data.success) {
          shsTracks.value = response.data.tracks
        } else {
          shsTracksError.value = response.data.message
        }
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка загрузки SHS треков',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const addShsTrack = async (shsTrack) => {
      let id = router.currentRoute.params.id
      blockData()
      try {
        await store.dispatch('artists/addTrack', {id, queryParams: {title: shsTrack.shs_title || shsTrack.title}})
        toast({
          component: ToastificationContent,
          props: {
            title: 'Трек добавлен',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        await fetchShsTracks()
        refreshData()
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка добавления трека',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const artistTracksForLink = computed(() => {
      if (!artistDetail.value || !artistDetail.value.tracks) return []
      const sorted = artistDetail.value.tracks
          .slice()
          .sort((a, b) => (a.track || a.title).localeCompare(b.track || b.title))

      if (!linkShsTrackItem.value) {
        return sorted.map(t => ({ label: t.track || t.title, value: t.id }))
      }

      const shsTitle = (linkShsTrackItem.value.shs_title || linkShsTrackItem.value.title || '').toLowerCase()

      // Find insertion point in sorted array
      let insertIdx = sorted.findIndex(t => (t.track || t.title).toLowerCase().localeCompare(shsTitle) >= 0)
      if (insertIdx === -1) insertIdx = sorted.length

      // Collect nearby tracks (up to 3 before and 3 after the insertion point)
      const nearbyIds = new Set()
      const nearby = []
      for (let i = Math.max(0, insertIdx - 3); i <= Math.min(sorted.length - 1, insertIdx + 2); i++) {
        nearby.push(sorted[i])
        nearbyIds.add(sorted[i].id)
      }

      const rest = sorted.filter(t => !nearbyIds.has(t.id))

      return [
        ...nearby.map(t => ({ label: `⭐ ${t.track || t.title}`, value: t.id })),
        ...rest.map(t => ({ label: t.track || t.title, value: t.id })),
      ]
    })

    const openLinkShsTrackModal = (shsTrack) => {
      linkShsTrackItem.value = shsTrack
      selectedTrackIdForLink.value = null
      showLinkShsTrackModal.value = true
    }

    const linkShsTrack = async () => {
      if (!selectedTrackIdForLink.value || !linkShsTrackItem.value) return
      blockData()
      try {
        await store.dispatch('artists/linkShsTrack', {
          id: selectedTrackIdForLink.value,
          shs_id: linkShsTrackItem.value.shs_id,
        })
        toast({
          component: ToastificationContent,
          props: {
            title: 'Трек привязан',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        showLinkShsTrackModal.value = false
        await fetchShsTracks()
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка привязки трека',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const fetchAliases = async () => {
      let id = router.currentRoute.params.id
      blockData()
      try {
        const response = await store.dispatch('artists/fetchArtistAliases', {id})
        artistAliases.value = response.data.data
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error loading aliases',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const searchForAliases = async () => {
      blockData()
      try {
        const response = await store.dispatch('artists/searchArtists', {q: artistDetail.value.title})
        const existingNames = artistAliases.value.map(a => a.alias)
        aliasSearchResults.value = response.data.filter(a => !a.db && !existingNames.includes(a.name) && a.name !== artistDetail.value.title)
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error searching aliases',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const addManualAlias = async () => {
      const name = manualAliasName.value.trim()
      if (!name) return
      let id = router.currentRoute.params.id
      blockData()
      try {
        await store.dispatch('artists/addAlias', {id, queryParams: {alias: name}})
        await fetchAliases()
        manualAliasName.value = ''
        toast({
          component: ToastificationContent,
          props: {
            title: 'Алиас добавлен',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error adding alias',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const toggleCoverArtist = async (value) => {
      let id = router.currentRoute.params.id
      blockData()
      try {
        await store.dispatch('artists/updateArtist', {id, queryParams: {is_cover_artist: value}})
        artistDetail.value.is_cover_artist = value
        toast({
          component: ToastificationContent,
          props: {
            title: value ? 'Отмечен как кавер-артист' : 'Снята отметка кавер-артиста',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка обновления',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const toggleSkipOriginals = async (value) => {
      let id = router.currentRoute.params.id
      blockData()
      try {
        await store.dispatch('artists/updateArtist', {id, queryParams: {skip_originals: value}})
        artistDetail.value.skip_originals = value
        toast({
          component: ToastificationContent,
          props: {
            title: value ? 'Оригиналы будут пропускаться' : 'Оригиналы не будут пропускаться',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка обновления',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const difficultyTooltip = '<strong>1</strong> — Все знают, легко берется вокал<br>'
      + '<strong>2</strong> — Широко известна, узнаваемый вокал, заезженные хиты<br>'
      + '<strong>3</strong> — Широко известна в своем жанре, узнаваемый вокал, топ-хиты<br>'
      + '<strong>4</strong> — Вспоминаемая группа с топ-хитами<br>'
      + '<strong>5</strong> — Вспоминаемая группа с топ-хитом или несколькими, жанровая<br>'
      + '<strong>6</strong> — Вспоминаемая группа с хитом или несколькими<br>'
      + '<strong>7</strong> — Забытая группа с топ-хитом прошлого<br>'
      + '<strong>8</strong> — Малоизвестная группа, есть хит, но не топ<br>'
      + '<strong>9</strong> — Нишевая группа, где-то светилась<br>'
      + '<strong>10</strong> — Слабо известная группа, нишевая'

    const updateDifficultyRating = async (rating) => {
      let id = router.currentRoute.params.id
      try {
        await store.dispatch('artists/updateArtist', {id, queryParams: {difficulty_rating: rating}})
        artistDetail.value.difficulty_rating = rating
        toast({
          component: ToastificationContent,
          props: {
            title: 'Сложность обновлена',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка обновления',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const startEditingTitle = () => {
      editTitle.value = artistDetail.value.title
      isEditingTitle.value = true
    }

    const cancelEditingTitle = () => {
      isEditingTitle.value = false
    }

    const saveTitle = async () => {
      const newTitle = editTitle.value.trim()
      if (!newTitle || newTitle === artistDetail.value.title) {
        isEditingTitle.value = false
        return
      }
      let id = router.currentRoute.params.id
      blockData()
      try {
        await store.dispatch('artists/updateArtist', {id, queryParams: {title: newTitle}})
        artistDetail.value.title = newTitle
        isEditingTitle.value = false
        toast({
          component: ToastificationContent,
          props: {
            title: 'Название обновлено',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка обновления названия',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }

    const addAliasFromSearch = async (name) => {
      let id = router.currentRoute.params.id
      blockData()
      try {
        await store.dispatch('artists/addAlias', {id, queryParams: {alias: name}})
        await fetchAliases()
        aliasSearchResults.value = aliasSearchResults.value.filter(r => r.name !== name)
        toast({
          component: ToastificationContent,
          props: {
            title: 'Алиас добавлен',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error adding alias',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        unblockData()
      }
    }


    const loadSavedCovers = async () => {
      if (savedCoversLoaded.value) return
      const id = router.currentRoute.params.id
      isLoadingSavedCovers.value = true
      try {
        const response = await store.dispatch('artists/fetchArtistCovers', {id})
        savedCovers.value = response.data
        savedCoversLoaded.value = true
      } catch (error) {
        // silent
      } finally {
        isLoadingSavedCovers.value = false
      }
    }

    const savedCoverNames = computed(() => {
      const set = new Set()
      savedCovers.value.forEach(c => {
        if (c.name) set.add(c.name.toLowerCase().trim())
      })
      return set
    })

    const isCoverSaved = (album) => {
      if (!album.name) return false
      return savedCoverNames.value.has(album.name.toLowerCase().trim())
    }

    const searchSpotifyCovers = async () => {
      let id = router.currentRoute.params.id
      isLoadingCovers.value = true
      coversError.value = ''
      coversSearched.value = false
      try {
        const response = await store.dispatch('artists/searchArtistAlbums', {id})
        spotifyCovers.value = response.data
        coversSearched.value = true
      } catch (error) {
        const msg = error.response && error.response.data && error.response.data.error
        coversError.value = msg || 'Ошибка поиска обложек'
        toast({
          component: ToastificationContent,
          props: {
            title: msg || 'Ошибка поиска обложек',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoadingCovers.value = false
      }
    }

    const searchYandexCovers = async () => {
      let id = router.currentRoute.params.id
      isLoadingYandexCovers.value = true
      yandexCoversError.value = ''
      yandexCoversSearched.value = false
      try {
        const response = await store.dispatch('artists/searchArtistYandexAlbums', {id})
        yandexCovers.value = response.data
        yandexCoversSearched.value = true
      } catch (error) {
        const msg = error.response && error.response.data && error.response.data.error
        yandexCoversError.value = msg || 'Ошибка поиска обложек'
        toast({
          component: ToastificationContent,
          props: {
            title: msg || 'Ошибка поиска обложек',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoadingYandexCovers.value = false
      }
    }

    const formatReleaseDate = (date, precision) => {
      if (!date) return ''
      const parts = date.split('-')
      if (precision === 'year' || parts.length === 1) return parts[0]
      if (precision === 'month' || parts.length === 2) {
        const months = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек']
        const monthIndex = parseInt(parts[1], 10) - 1
        return `${months[monthIndex]} ${parts[0]}`
      }
      const d = new Date(date)
      return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' })
    }

    const albumTypeLabels = { album: 'Альбом', single: 'Сингл', compilation: 'Сборник' }
    const albumTypeIcons = { album: 'DiscIcon', single: 'FileIcon', compilation: 'LayersIcon' }
    const albumTypeVariants = { album: 'primary', single: 'info', compilation: 'warning' }

    const albumTypeLabel = type => albumTypeLabels[type] || type
    const albumTypeIcon = type => albumTypeIcons[type] || 'DiscIcon'
    const albumTypeVariant = type => albumTypeVariants[type] || 'secondary'

    const currentFullCover = computed(() => {
      const i = fullCoverIndex.value
      return i >= 0 && i < savedCovers.value.length ? savedCovers.value[i] : null
    })

    const openFullCover = (index) => {
      fullCoverIndex.value = index
      showFullCover.value = true
    }

    const goPrevCover = () => {
      if (fullCoverIndex.value > 0) {
        fullCoverIndex.value -= 1
      }
    }

    const goNextCover = () => {
      if (fullCoverIndex.value < savedCovers.value.length - 1) {
        fullCoverIndex.value += 1
      }
    }

    const openCoverPreview = (album) => {
      previewAlbum.value = album
      showCoverPreview.value = true
    }

    const loadAlbumTracks = async (album, source) => {
      if (albumTracks.value[album.id]) return
      if (albumTracksLoading.value[album.id]) return
      const id = router.currentRoute.params.id
      albumTracksLoading.value = { ...albumTracksLoading.value, [album.id]: true }
      try {
        const response = await store.dispatch('artists/fetchAlbumTracks', {
          id,
          source,
          albumId: album.id,
        })
        // adding — реактивный флаг блокировки кнопки добавления (Vue 2: свойство
        // должно существовать заранее, иначе мутация не отслеживается).
        const tracks = (response.data || []).map(track => ({ ...track, adding: false }))
        albumTracks.value = { ...albumTracks.value, [album.id]: tracks }
      } catch (error) {
        const next = { ...albumTracks.value }
        delete next[album.id]
        albumTracks.value = next
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка загрузки треков альбома',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        albumTracksLoading.value = { ...albumTracksLoading.value, [album.id]: false }
      }
    }

    // Пересчитать число колонок по фактической ширине контейнера сетки.
    // Карточка 220px + gap 1rem (16px). Игнорируем нулевую ширину (скрытая вкладка).
    const measureAlbumColumns = () => {
      const el = albumsContainer.value
      if (!el || !el.clientWidth) return
      const cardWidth = 220
      const gap = 16
      albumColumns.value = Math.max(1, Math.floor((el.clientWidth + gap) / (cardWidth + gap)))
    }

    // Разбить группу альбомов на строки по числу колонок.
    const albumRows = (group) => {
      const size = albumColumns.value
      const rows = []
      for (let i = 0; i < group.length; i += size) {
        rows.push(group.slice(i, i + size))
      }
      return rows
    }

    // Содержит ли строка активный альбом данного источника.
    const rowHasActiveAlbum = (row, source) => {
      const active = activeAlbum.value
      return !!active && active.source === source && row.some(album => album.id === active.id)
    }

    const toggleAlbumTracks = (album, source) => {
      const isActive = activeAlbum.value
          && activeAlbum.value.source === source
          && activeAlbum.value.id === album.id
      if (isActive) {
        activeAlbum.value = null
        return
      }
      measureAlbumColumns()
      activeAlbum.value = { source, id: album.id, name: album.name }
      loadAlbumTracks(album, source)
    }

    const closeAlbumTracks = () => {
      activeAlbum.value = null
    }

    // ResizeObserver: пересчитывает колонки при появлении вкладки (0 → ширина)
    // и при любом изменении ширины контейнера сетки.
    let albumsResizeObserver = null
    onMounted(() => {
      if (typeof ResizeObserver !== 'undefined' && albumsContainer.value) {
        albumsResizeObserver = new ResizeObserver(() => measureAlbumColumns())
        albumsResizeObserver.observe(albumsContainer.value)
      } else {
        window.addEventListener('resize', measureAlbumColumns)
      }
    })
    onUnmounted(() => {
      if (albumsResizeObserver) {
        albumsResizeObserver.disconnect()
        albumsResizeObserver = null
      } else {
        window.removeEventListener('resize', measureAlbumColumns)
      }
    })

    // При появлении/смене списков обложек пересчитать колонки после отрисовки,
    // иначе первая отрисовка идёт со стартовым albumColumns = 1 (один столбец).
    watch([spotifyCovers, yandexCovers], () => {
      nextTick(() => measureAlbumColumns())
    })

    const addAlbumTrack = async (track) => {
      if (track.adding) return
      const id = router.currentRoute.params.id
      track.adding = true
      try {
        await store.dispatch('artists/addTrack', { id, queryParams: { title: track.title } })
        track.in_db = true
        toast({
          component: ToastificationContent,
          props: {
            title: 'Трек добавлен',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка добавления трека',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        track.adding = false
      }
    }

    const saveCover = async (album) => {
      let id = router.currentRoute.params.id
      try {
        const response = await store.dispatch('artists/saveArtistCover', {
          id,
          queryParams: {
            cover: album.cover,
            name: album.name,
          },
        })
        if (response.data) {
          savedCovers.value.push(response.data)
        } else {
          savedCovers.value.push({name: album.name, url: album.cover})
        }
        toast({
          component: ToastificationContent,
          props: {
            title: 'Обложка сохранена',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка сохранения обложки',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    }

    const deleteCover = async (cover) => {
      if (!confirm('Удалить обложку?')) return
      const id = router.currentRoute.params.id
      try {
        await store.dispatch('artists/deleteArtistCover', {id, coverId: cover.id})
        savedCovers.value = savedCovers.value.filter(c => c.id !== cover.id)
        toast({
          component: ToastificationContent,
          props: {
            title: 'Обложка удалена',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка удаления обложки',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    }

    const loadSavedPhotos = async () => {
      if (savedPhotosLoaded.value) return
      const id = router.currentRoute.params.id
      isLoadingSavedPhotos.value = true
      try {
        const response = await store.dispatch('artists/fetchArtistPhotos', {id})
        savedPhotos.value = response.data
        savedPhotosLoaded.value = true
      } catch (error) {
        // silent
      } finally {
        isLoadingSavedPhotos.value = false
      }
    }

    const searchPhotos = async (start = 1, query = '', type = null) => {
      const id = router.currentRoute.params.id
      isLoadingPhotos.value = true
      photosError.value = ''
      if (start === 1) {
        photoResults.value = []
        photosSearched.value = false
        activePhotoQuery.value = query
        activePhotoType.value = type
      }
      try {
        const response = await store.dispatch('artists/searchArtistPhotos', {id, start, query: activePhotoQuery.value})
        // Проставляем тип из активного пресета, чтобы он сохранился вместе с фото.
        const items = (response.data.items || []).map(item => ({...item, type: activePhotoType.value}))
        photoResults.value = photoResults.value.concat(items)
        photoNextStart.value = response.data.next_start || null
        photosSearched.value = true
      } catch (error) {
        const msg = error.response && error.response.data && error.response.data.error
        photosError.value = msg || 'Ошибка поиска фото'
        toast({
          component: ToastificationContent,
          props: {title: msg || 'Ошибка поиска фото', icon: 'AlertTriangleIcon', variant: 'danger'},
        })
      } finally {
        isLoadingPhotos.value = false
      }
    }

    const loadMorePhotos = () => {
      if (photoNextStart.value) searchPhotos(photoNextStart.value)
    }

    const openPhotoModal = (photo, isResult) => {
      currentPhoto.value = {...photo, isResult}
      savePhotoError.value = ''
      showPhotoModal.value = true
    }

    // Достаёт человекочитаемое сообщение об ошибке из ответа бэкенда/таймаута.
    const extractPhotoError = (error) => {
      const data = error.response && error.response.data
      let msg = (data && data.message) || ''
      if (!msg && data && data.errors) {
        const first = Object.values(data.errors)[0]
        msg = Array.isArray(first) ? first[0] : first
      }
      if (!msg && error.code === 'ECONNABORTED') {
        msg = 'Превышено время ожидания (15 с)'
      }
      return msg || 'Ошибка сохранения фото'
    }

    // Высокое разрешение: больше 1000 px по вертикали или горизонтали.
    const isHighResPhoto = (photo) => !!photo && (photo.width > 1000 || photo.height > 1000)

    // Множество исходных ссылок уже сохранённых фото — для пометки дублей в выдаче.
    const savedPhotoUrls = computed(() => new Set(savedPhotos.value.map(p => p.image_url).filter(Boolean)))
    const isPhotoSaved = (photo) => !!photo && savedPhotoUrls.value.has(photo.image_url)

    const savePhoto = async (photo) => {
      const id = router.currentRoute.params.id
      isSavingPhoto.value = true
      savePhotoError.value = ''
      try {
        const response = await store.dispatch('artists/saveArtistPhoto', {
          id,
          queryParams: {
            image_url: photo.image_url,
            name: photo.title ? photo.title.slice(0, 255) : photo.title,
            type: photo.type || null,
            width: photo.width,
            height: photo.height,
          },
        })
        if (response.data && response.data.photo) {
          savedPhotos.value.unshift(response.data.photo)
        }
        showPhotoModal.value = false
        toast({
          component: ToastificationContent,
          props: {title: 'Фото сохранено', icon: 'CheckIcon', variant: 'success'},
        })
      } catch (error) {
        let msg = extractPhotoError(error)
        if (error.code === 'ECONNABORTED') {
          msg = 'Превышено время ожидания (15 с) — попробуйте «Через сервер»'
        }
        savePhotoError.value = msg
      } finally {
        isSavingPhoto.value = false
      }
    }

    const onPhotoSaved = (response) => {
      if (response.data && response.data.photo) {
        savedPhotos.value.unshift(response.data.photo)
      }
      showPhotoModal.value = false
      toast({
        component: ToastificationContent,
        props: {title: 'Фото сохранено', icon: 'CheckIcon', variant: 'success'},
      })
    }

    // Фолбэк 1: сервер сам качает картинку с браузероподобными заголовками —
    // обходит hotlink-защиту, не зависит от CORS.
    const savePhotoViaProxy = async (photo) => {
      const id = router.currentRoute.params.id
      isSavingPhoto.value = true
      savePhotoError.value = ''
      try {
        const response = await store.dispatch('artists/proxyArtistPhoto', {
          id,
          queryParams: {
            image_url: photo.image_url,
            name: photo.title ? photo.title.slice(0, 255) : photo.title,
            type: photo.type || null,
            width: photo.width,
            height: photo.height,
          },
        })
        onPhotoSaved(response)
      } catch (error) {
        savePhotoError.value = extractPhotoError(error)
      } finally {
        isSavingPhoto.value = false
      }
    }

    // Фолбэк 2: браузер сам скачивает уже отображаемую картинку (fetch → blob)
    // и грузит её файлом. Сохраняет исходный формат. Работает, если хост отдаёт CORS.
    const savePhotoViaBrowser = async (photo) => {
      const id = router.currentRoute.params.id
      isSavingPhoto.value = true
      savePhotoError.value = ''
      const controller = new AbortController()
      const timer = setTimeout(() => controller.abort(), 15000)
      try {
        const res = await fetch(photo.image_url, {signal: controller.signal})
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`)
        }
        const blob = await res.blob()
        const extension = (blob.type && blob.type.split('/')[1]) || 'jpg'
        const formData = new FormData()
        formData.append('file', blob, `photo.${extension}`)
        if (photo.image_url) formData.append('image_url', photo.image_url)
        if (photo.title) formData.append('name', photo.title.slice(0, 255))
        if (photo.type) formData.append('type', photo.type)
        if (photo.width) formData.append('width', photo.width)
        if (photo.height) formData.append('height', photo.height)
        const response = await store.dispatch('artists/uploadArtistPhoto', {id, formData})
        onPhotoSaved(response)
      } catch (error) {
        if (error.name === 'AbortError') {
          savePhotoError.value = 'Превышено время загрузки (15 с)'
        } else if (error.response) {
          savePhotoError.value = extractPhotoError(error)
        } else {
          savePhotoError.value = 'Браузер не смог скачать картинку (CORS/недоступно)'
        }
      } finally {
        clearTimeout(timer)
        isSavingPhoto.value = false
      }
    }

    const deletePhoto = async (photo) => {
      if (!confirm('Удалить фото?')) return
      const id = router.currentRoute.params.id
      try {
        await store.dispatch('artists/deleteArtistPhoto', {id, photoId: photo.id})
        savedPhotos.value = savedPhotos.value.filter(p => p.id !== photo.id)
        showPhotoModal.value = false
        toast({
          component: ToastificationContent,
          props: {title: 'Фото удалено', icon: 'CheckIcon', variant: 'success'},
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {title: 'Ошибка удаления фото', icon: 'AlertTriangleIcon', variant: 'danger'},
        })
      }
    }

    const deleteCurrentCover = async () => {
      const cover = currentFullCover.value
      if (!cover) return
      await deleteCover(cover)
      showFullCover.value = false
    }

    const onFullCoverKeydown = (event) => {
      if (event.key === 'ArrowLeft') {
        goPrevCover()
      } else if (event.key === 'ArrowRight') {
        goNextCover()
      }
    }

    const onFullCoverShown = () => {
      window.addEventListener('keydown', onFullCoverKeydown)
    }

    const onFullCoverHidden = () => {
      window.removeEventListener('keydown', onFullCoverKeydown)
    }

    const updateCoverDifficulty = async (cover, value) => {
      const id = router.currentRoute.params.id
      try {
        await store.dispatch('artists/updateArtistCover', {
          id,
          coverId: cover.id,
          queryParams: { difficulty: value || null },
        })
        cover.difficulty = value
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка обновления сложности',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    }

    const saveCoverWithUrl = async (imageUrl, done, difficulty) => {
      let id = router.currentRoute.params.id
      const albumName = previewAlbum.value ? previewAlbum.value.name : ''
      try {
        await store.dispatch('artists/saveArtistCover', {
          id,
          queryParams: {
            cover: imageUrl,
            name: albumName,
            difficulty: difficulty || null,
          },
        })
        toast({
          component: ToastificationContent,
          props: {
            title: 'Обложка сохранена',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        showCoverPreview.value = false
        savedCoversLoaded.value = false
        await loadSavedCovers()
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка сохранения обложки',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        if (done) done()
      }
    }

    const openExternal = (url) => {
      window.open(url, '_blank')
    }

    return {
      openExternal,
      ignoreTracks,
      formatDate,
      artistDetail,
      similar,
      additional,
      isLoadingArtist,
      isLoadingStaff,
      disabledArtistId,
      refreshData,
      addArtist,
      blockData,
      unblockData,
      tableColumns,
      badWord,
      formatNumber,
      deleteTrack,
      flushCache,
      loadAdditionalTracks,
      trackSearchQuery,
      searchTracksByName,
      loadLastfmInfo,
      addTrack,
      toggleIgnore,
      artistAliases,
      aliasSearchResults,
      fetchAliases,
      searchForAliases,
      addAliasFromSearch,
      manualAliasName,
      addManualAlias,
      shsTracks,
      shsTracksError,
      shsTableColumns,
      mergedShsTracks,
      fetchShsTracks,
      addShsTrack,
      showLinkShsTrackModal,
      linkShsTrackItem,
      selectedTrackIdForLink,
      artistTracksForLink,
      openLinkShsTrackModal,
      linkShsTrack,
      showShsSyncButton,
      shsSyncDisabled,
      showShsTracksButton,
      syncShs,
      syncSpotify,
      syncYandex,
      syncWikipedia,
      syncShsTracks,
      showShsCandidates,
      shsCandidates,
      selectShsCandidate,
      toggleCoverArtist,
      toggleSkipOriginals,
      updateDifficultyRating,
      difficultyTooltip,
      aliasesText,
      showAddExternalIdModal,
      externalIdInput,
      isAddingExternalId,
      addExternalId,
      deleteExternalId,
      wikipediaUrl,
      wikipediaLabel,
      isEditingTitle,
      editTitle,
      startEditingTitle,
      cancelEditingTitle,
      saveTitle,
      toStarRating,
      savedCovers,
      isLoadingSavedCovers,
      spotifyCovers,
      groupedSpotifyCovers,
      isLoadingCovers,
      coversError,
      coversSearched,
      showCoverPreview,
      previewAlbum,
      showFullCover,
      fullCoverIndex,
      currentFullCover,
      openFullCover,
      goPrevCover,
      goNextCover,
      deleteCurrentCover,
      onFullCoverShown,
      onFullCoverHidden,
      hasSpotifyId,
      loadSavedCovers,
      searchSpotifyCovers,
      yandexCovers,
      groupedYandexCovers,
      isLoadingYandexCovers,
      yandexCoversError,
      yandexCoversSearched,
      hasYandexId,
      searchYandexCovers,
      isCoverSaved,
      openCoverPreview,
      activeAlbum,
      albumTracks,
      albumTracksLoading,
      albumsContainer,
      albumRows,
      rowHasActiveAlbum,
      toggleAlbumTracks,
      closeAlbumTracks,
      addAlbumTrack,
      saveCover,
      deleteCover,
      updateCoverDifficulty,
      saveCoverWithUrl,
      savedPhotos,
      savedPhotosLoaded,
      isLoadingSavedPhotos,
      photoResults,
      isLoadingPhotos,
      photosSearched,
      photosError,
      photoNextStart,
      showPhotoModal,
      currentPhoto,
      isSavingPhoto,
      savePhotoError,
      activePhotoQuery,
      activePhotoType,
      photoQueryPresets,
      photoTypeLabel,
      isHighResPhoto,
      isPhotoSaved,
      loadSavedPhotos,
      searchPhotos,
      loadMorePhotos,
      openPhotoModal,
      savePhoto,
      savePhotoViaProxy,
      savePhotoViaBrowser,
      deletePhoto,
      formatReleaseDate,
      albumTypeLabel,
      albumTypeIcon,
      albumTypeVariant,
      currentMembers,
      formerMembers,
      isLoadingMembers,
      isSavingMembers,
      newCurrentMember,
      newFormerMember,
      hasMembers,
      initMembers,
      loadMembers,
      saveMembers,
      removeMember,
      moveToFormer,
      moveToCurrent,
      addCurrentMember,
      addFormerMember,
      moveMemberUp,
      moveMemberDown,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-blog.scss';

.vs__dropdown-menu[style] {
  z-index: 1060 !important;
}
</style>
